import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import ManualEsim from './admin/ManualEsim';
import CountryNamesPanel from './admin/CountryNamesPanel';
import DestinationGroupingPanel from './admin/DestinationGroupingPanel';
import AssistantKbPanel from './admin/AssistantKbPanel';
import { AddComplimentaryEsimModal, ComplimentaryTopupModal } from './admin/ComplimentaryModals';
import BlogManager from './admin/BlogManager';
import ReactMarkdown from 'react-markdown';
import { 
  Users, Package, Tag, Percent, Trash2, Plus, ArrowLeft, Loader2, AlertCircle, 
  Check, DollarSign, X, ShoppingCart, Eye, Wifi, QrCode, Smartphone, Globe,
  ExternalLink, Copy, CheckCircle, Clock, MapPin, CreditCard, Wrench, BarChart3, TrendingUp,
  Shield, AlertTriangle, Calendar, ChevronLeft, ChevronRight, Mail, Send, Bold, Italic, 
  Underline, AlignLeft, AlignCenter, Link, Image, Type, Palette, Upload, Sparkles, Edit, Search, FileText, Layers, BookOpen
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { 
    user, isAdmin, 
    promoCodes, addPromoCode, deletePromoCode,
    markupSettings, updateMarkupSettings,
    getAllUsers, getAllOrders,
    allCountries, convertPrice,
    getAdminReferrals, getReferralSettings, updateReferralSettings, addUserCredits,
    getPromoAnalytics, getFeaturedDestinations, setFeaturedDestinations: saveFeaturedDestinations
  } = useApp();

  const [activeTab, setActiveTab] = useState('orders');
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  // Order pagination
  const [ordersPage, setOrdersPage] = useState(1);
  const ordersPerPage = 20;

  // Order detail modal
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderUsage, setOrderUsage] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  // ICCID edit (inside Order detail modal)
  const [editingIccid, setEditingIccid] = useState(false);
  const [iccidForm, setIccidForm] = useState({ iccid: '', smdp_address: '', matching_id: '' });
  const [iccidSaving, setIccidSaving] = useState(false);
  const [iccidError, setIccidError] = useState('');

  // Promo code form
  const [showPromoForm, setShowPromoForm] = useState(false);
  const [newPromoCode, setNewPromoCode] = useState('');
  const [newPromoDiscount, setNewPromoDiscount] = useState('');
  const [newPromoLocalDiscount, setNewPromoLocalDiscount] = useState('');
  const [newPromoRegionalDiscount, setNewPromoRegionalDiscount] = useState('');
  const [useSeparateDiscounts, setUseSeparateDiscounts] = useState(false);
  const [newPromoDesc, setNewPromoDesc] = useState('');
  const [promoValidFor, setPromoValidFor] = useState('all'); // 'all', 'local', 'regional', 'specific'
  const [promoCountries, setPromoCountries] = useState([]);
  const [promoCountrySearch, setPromoCountrySearch] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoMaxUses, setPromoMaxUses] = useState('');
  const [promoExpiryDate, setPromoExpiryDate] = useState('');
  const [promoMaxUsesPerUser, setPromoMaxUsesPerUser] = useState('');

  // Markup form - Grid structure
  const [markupGrid, setMarkupGrid] = useState({
    local_0_3: 40, local_3_6: 40, local_6_10: 40,
    local_10_15: 40, local_15_20: 40, local_20_plus: 40,
    regional_0_3: 25, regional_3_6: 25, regional_6_10: 25,
    regional_10_15: 25, regional_15_20: 25, regional_20_plus: 25,
    global_0_3: 20, global_3_6: 20, global_6_10: 20,
    global_10_15: 20, global_15_20: 20, global_20_plus: 20
  });
  const [markupLoading, setMarkupLoading] = useState(false);

  // Referral program state
  const [referralData, setReferralData] = useState({ referrals: [], stats: {} });
  const [referralSettings, setReferralSettings] = useState({ credit_value_aud: 5, referred_discount: 0.10, is_active: true });
  const [loadingReferrals, setLoadingReferrals] = useState(false);
  const [referralSettingsLoading, setReferralSettingsLoading] = useState(false);
  const [promoAnalytics, setPromoAnalytics] = useState(null);
  const [loadingPromoAnalytics, setLoadingPromoAnalytics] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({ firstName: '', lastName: '', email: '', password: '', sendWelcomeEmail: true });
  const [addingUser, setAddingUser] = useState(false);
  const [addUserError, setAddUserError] = useState('');
  const [viewOrdersUser, setViewOrdersUser] = useState(null);
  // Per-tab search filters (name or email; case-insensitive; part or full match).
  const [ordersSearch, setOrdersSearch] = useState('');
  // Profits tab filters
  const [profitsFilterType, setProfitsFilterType] = useState('all'); // all | paid | complimentary | manual
  const [profitsFilterCountry, setProfitsFilterCountry] = useState('all');
  const [profitsFilterFrom, setProfitsFilterFrom] = useState('');
  const [profitsFilterTo, setProfitsFilterTo] = useState('');
  const [profitsSearch, setProfitsSearch] = useState('');
  const [customersSearch, setCustomersSearch] = useState('');
  const [referralsSearch, setReferralsSearch] = useState('');
  // "Add complimentary eSIM" modal state (Customers tab → + button)
  const [complimentaryEsimUser, setComplimentaryEsimUser] = useState(null);
  // "Complimentary top-up" modal state (Order View modal → button)
  const [complimentaryTopupOrder, setComplimentaryTopupOrder] = useState(null);
  const [featuredDestinations, setFeaturedDestinations] = useState([]);
  const [featuredSearch, setFeaturedSearch] = useState('');
  const [savingFeatured, setSavingFeatured] = useState(false);
  const [showAddCreditsModal, setShowAddCreditsModal] = useState(false);
  const [selectedUserForCredits, setSelectedUserForCredits] = useState(null);
  const [creditsToAdd, setCreditsToAdd] = useState('');
  const [creditsReason, setCreditsReason] = useState('');

  // Delete user state
  const [deletingUser, setDeletingUser] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Delete order state
  const [deletingOrder, setDeletingOrder] = useState(null);
  const [showDeleteOrderConfirm, setShowDeleteOrderConfirm] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);

  // Traffic analytics state
  const [trafficData, setTrafficData] = useState(null);
  const [trafficPeriod, setTrafficPeriod] = useState('week');
  const [loadingTraffic, setLoadingTraffic] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  
  // Fraud monitoring state
  const [fraudData, setFraudData] = useState(null);
  const [loadingFraud, setLoadingFraud] = useState(false);

  // Blocked destinations state
  const [blockedDestinations, setBlockedDestinations] = useState([]);
  const [loadingBlocked, setLoadingBlocked] = useState(false);
  const [newBlockedCode, setNewBlockedCode] = useState('');
  const [newBlockedName, setNewBlockedName] = useState('');

  // Marketing email state
  const [marketingRecipients, setMarketingRecipients] = useState('all'); // 'all' or 'selected'
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailContent, setEmailContent] = useState('');
  const [emailImages, setEmailImages] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [emailPreviewHtml, setEmailPreviewHtml] = useState('');
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSentSuccess, setEmailSentSuccess] = useState(false);
  const [emailError, setEmailError] = useState('');

  // Check admin access
  useEffect(() => {
    if (!user) {
      navigate('/login');
    } else if (!isAdmin) {
      navigate('/dashboard');
    }
  }, [user, isAdmin, navigate]);

  // Lazy-load per-tab data to speed up initial page load. Orders power the
  // Orders, Customers and Profits tabs; the full user list is only needed by
  // Customers. Each dataset is fetched once, the first time a tab needs it.
  const ordersFetchedRef = useRef(false);
  const usersFetchedRef = useRef(false);
  useEffect(() => {
    if (!isAdmin) return;
    const needsOrders = ['orders', 'customers', 'profits'].includes(activeTab);
    if (needsOrders && !ordersFetchedRef.current) {
      ordersFetchedRef.current = true;
      setLoadingData(true);
      getAllOrders()
        .then((d) => setOrders(d))
        .catch((err) => { console.error('Error loading orders:', err); ordersFetchedRef.current = false; })
        .finally(() => setLoadingData(false));
    }
    if (activeTab === 'customers' && !usersFetchedRef.current) {
      usersFetchedRef.current = true;
      getAllUsers()
        .then((d) => setCustomers(d))
        .catch((err) => { console.error('Error loading users:', err); usersFetchedRef.current = false; });
    }
  }, [activeTab, isAdmin, getAllOrders, getAllUsers]);

  // Load referral data when tab changes
  useEffect(() => {
    const loadReferralData = async () => {
      if (activeTab === 'referrals' && isAdmin) {
        setLoadingReferrals(true);
        try {
          const [refData, refSettings] = await Promise.all([
            getAdminReferrals(),
            getReferralSettings()
          ]);
          setReferralData(refData);
          setReferralSettings(refSettings);
        } catch (err) {
          console.error('Error loading referral data:', err);
        } finally {
          setLoadingReferrals(false);
        }
      }
    };
    loadReferralData();
  }, [activeTab, isAdmin, getAdminReferrals, getReferralSettings]);

  // Load promo analytics when tab changes
  useEffect(() => {
    const loadPromoAnalytics = async () => {
      if (activeTab === 'promos' && isAdmin) {
        setLoadingPromoAnalytics(true);
        try {
          const data = await getPromoAnalytics();
          if (data && data.success) setPromoAnalytics(data);
        } catch (err) {
          console.error('Error loading promo analytics:', err);
        } finally {
          setLoadingPromoAnalytics(false);
        }
      }
    };
    loadPromoAnalytics();
  }, [activeTab, isAdmin, getPromoAnalytics]);

  // Load featured destinations when tab changes
  useEffect(() => {
    const loadFeatured = async () => {
      if (activeTab === 'featured' && isAdmin) {
        const data = await getFeaturedDestinations();
        if (data && data.success) setFeaturedDestinations(data.destinations);
      }
    };
    loadFeatured();
  }, [activeTab, isAdmin, getFeaturedDestinations]);

  // Load traffic data when tab changes or period changes
  useEffect(() => {
    const loadTrafficData = async () => {
      if (activeTab === 'traffic' && isAdmin) {
        setLoadingTraffic(true);
        setLoadingFraud(true);
        try {
          // Build query params
          let url = `${API}/admin/traffic?period=${trafficPeriod}`;
          if (trafficPeriod === 'custom' && customStartDate && customEndDate) {
            url += `&start_date=${customStartDate}&end_date=${customEndDate}`;
          }
          
          const response = await axios.get(url);
          if (response.data.success) {
            setTrafficData(response.data);
          }
          
          // Also load fraud data with same date range
          let fraudUrl = `${API}/admin/fraud-attempts?period=${trafficPeriod}`;
          if (trafficPeriod === 'custom' && customStartDate && customEndDate) {
            fraudUrl += `&start_date=${customStartDate}&end_date=${customEndDate}`;
          }
          
          const fraudResponse = await axios.get(fraudUrl);
          if (fraudResponse.data.success) {
            setFraudData(fraudResponse.data);
          }
        } catch (err) {
          console.error('Error loading traffic/fraud data:', err);
        } finally {
          setLoadingTraffic(false);
          setLoadingFraud(false);
        }
      }
    };
    loadTrafficData();
  }, [activeTab, trafficPeriod, customStartDate, customEndDate, isAdmin]);

  // Load blocked destinations when tab changes
  useEffect(() => {
    const loadBlockedDestinations = async () => {
      if (activeTab === 'blocked' && isAdmin) {
        setLoadingBlocked(true);
        try {
          const response = await axios.get(`${API}/admin/blocked-destinations`);
          if (response.data.success) {
            setBlockedDestinations(response.data.blocked_destinations || []);
          }
        } catch (err) {
          console.error('Error loading blocked destinations:', err);
        } finally {
          setLoadingBlocked(false);
        }
      }
    };
    loadBlockedDestinations();
  }, [activeTab, isAdmin]);

  // Packages cache status (auto-polled while tab is open)
  const [cacheStatus, setCacheStatus] = useState(null);
  const [cacheRefreshing, setCacheRefreshing] = useState(false);
  const [roamifyDiag, setRoamifyDiag] = useState(null);
  const [roamifyDiagLoading, setRoamifyDiagLoading] = useState(false);
  const [feedHealth, setFeedHealth] = useState(null);
  const [feedHealthLoading, setFeedHealthLoading] = useState(false);
  const runFeedHealth = async () => {
    setFeedHealthLoading(true);
    setFeedHealth(null);
    try {
      const r = await axios.get(`${API}/admin/feed-health`);
      setFeedHealth(r.data);
    } catch (err) {
      setFeedHealth({ error: err.response?.data?.detail || err.message || 'Feed health check failed' });
    } finally {
      setFeedHealthLoading(false);
    }
  };
  const runRoamifyDiagnostic = async () => {
    setRoamifyDiagLoading(true);
    setRoamifyDiag(null);
    try {
      const r = await axios.get(`${API}/admin/roamify-diagnostic`);
      setRoamifyDiag(r.data);
    } catch (err) {
      setRoamifyDiag({ error: err.response?.data?.detail || err.message || 'Diagnostic failed' });
    } finally {
      setRoamifyDiagLoading(false);
    }
  };
  useEffect(() => {
    if (activeTab !== 'cache' || !isAdmin) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const response = await axios.get(`${API}/admin/packages-cache/status`);
        if (!cancelled && response.data.success) {
          setCacheStatus(response.data);
          setCacheRefreshing(response.data.status?.state === 'running');
        }
      } catch (err) {
        console.error('Error loading cache status:', err);
      }
    };
    load();
    const interval = setInterval(load, 3000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [activeTab, isAdmin]);

  const handleRefreshPackagesCache = async () => {
    try {
      setCacheRefreshing(true);
      const response = await axios.post(`${API}/admin/packages-cache/refresh`);
      if (response.data.success) {
        setCacheStatus((prev) => prev ? { ...prev, status: response.data.status } : prev);
      } else {
        alert(response.data.message || 'Refresh already running');
      }
    } catch (err) {
      console.error('Error triggering refresh:', err);
      alert('Failed to start refresh');
      setCacheRefreshing(false);
    }
  };

  // Welcome Popup state
  const [popupConfig, setPopupConfig] = useState({
    enabled: false,
    title: '',
    body: '',
    image_url: '',
    button_text: "Let's Go!",
    show_to_logged_in: true,
  });
  const [popupLoading, setPopupLoading] = useState(false);
  const [popupSaving, setPopupSaving] = useState(false);
  const [popupSaved, setPopupSaved] = useState(false);
  const [popupImageUploading, setPopupImageUploading] = useState(false);
  const [popupPreview, setPopupPreview] = useState(false);

  // Welcome-Back (returning-user greeting) state
  const [wbConfig, setWbConfig] = useState({
    enabled: true,
    greeting: 'Still in {country}?',
    subtext: 'Jump straight back to your local eSIM plan.',
    button_text: 'View local plans',
    inactivity_days: 30,
    trigger_on_location_change: true,
    trigger_on_inactivity: true,
  });
  const [wbLoading, setWbLoading] = useState(false);
  const [wbSaving, setWbSaving] = useState(false);
  const [wbSaved, setWbSaved] = useState(false);
  const [wbAnalytics, setWbAnalytics] = useState(null);

  useEffect(() => {
    if (activeTab !== 'welcome-back' || !isAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        setWbLoading(true);
        const [res, an] = await Promise.all([
          axios.get(`${API}/admin/welcome-back-settings`),
          axios.get(`${API}/admin/welcome-back/analytics`).catch(() => ({ data: null })),
        ]);
        if (!cancelled) {
          setWbConfig({
            enabled: res.data.enabled !== false,
            greeting: res.data.greeting || 'Still in {country}?',
            subtext: res.data.subtext || '',
            button_text: res.data.button_text || 'View local plans',
            inactivity_days: res.data.inactivity_days || 30,
            trigger_on_location_change: res.data.trigger_on_location_change !== false,
            trigger_on_inactivity: res.data.trigger_on_inactivity !== false,
          });
          if (an && an.data && an.data.success) setWbAnalytics(an.data);
        }
      } catch (err) {
        console.error('Error loading welcome-back settings:', err);
      } finally {
        if (!cancelled) setWbLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, isAdmin]);

  const handleSaveWelcomeBack = async () => {
    try {
      setWbSaving(true);
      setWbSaved(false);
      const res = await axios.put(`${API}/admin/welcome-back-settings`, wbConfig);
      if (res.data.success) {
        setWbSaved(true);
        setTimeout(() => setWbSaved(false), 2500);
      }
    } catch (err) {
      console.error('Error saving welcome-back settings:', err);
      alert(err.response?.data?.detail || 'Failed to save welcome-back settings');
    } finally {
      setWbSaving(false);
    }
  };

  useEffect(() => {
    if (activeTab !== 'welcome-popup' || !isAdmin) return;
    let cancelled = false;
    const load = async () => {
      try {
        setPopupLoading(true);
        const response = await axios.get(`${API}/admin/welcome-popup`);
        if (!cancelled) {
          setPopupConfig({
            enabled: !!response.data.enabled,
            title: response.data.title || '',
            body: response.data.body || '',
            image_url: response.data.image_url || '',
            button_text: response.data.button_text || "Let's Go!",
            show_to_logged_in: response.data.show_to_logged_in !== false,
          });
        }
      } catch (err) {
        console.error('Error loading welcome popup config:', err);
      } finally {
        if (!cancelled) setPopupLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [activeTab, isAdmin]);

  const handleSavePopup = async () => {
    try {
      setPopupSaving(true);
      setPopupSaved(false);
      const response = await axios.put(`${API}/admin/welcome-popup`, popupConfig);
      if (response.data.success) {
        setPopupSaved(true);
        setTimeout(() => setPopupSaved(false), 2500);
      }
    } catch (err) {
      console.error('Error saving popup config:', err);
      alert(err.response?.data?.detail || 'Failed to save popup configuration');
    } finally {
      setPopupSaving(false);
    }
  };

  const handlePopupImageUpload = async (file) => {
    if (!file) return;
    try {
      setPopupImageUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/admin/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response.data.success && response.data.url) {
        setPopupConfig((prev) => ({ ...prev, image_url: response.data.url }));
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      alert(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setPopupImageUploading(false);
    }
  };

  // Update markup state when settings change
  useEffect(() => {
    setMarkupGrid({
      local_0_3: markupSettings.local_0_3 || 40,
      local_3_6: markupSettings.local_3_6 || 40,
      local_6_10: markupSettings.local_6_10 || 40,
      local_10_15: markupSettings.local_10_15 || 40,
      local_15_20: markupSettings.local_15_20 || 40,
      local_20_plus: markupSettings.local_20_plus || 40,
      regional_0_3: markupSettings.regional_0_3 || 25,
      regional_3_6: markupSettings.regional_3_6 || 25,
      regional_6_10: markupSettings.regional_6_10 || 25,
      regional_10_15: markupSettings.regional_10_15 || 25,
      regional_15_20: markupSettings.regional_15_20 || 25,
      regional_20_plus: markupSettings.regional_20_plus || 25,
      global_0_3: markupSettings.global_0_3 || 20,
      global_3_6: markupSettings.global_3_6 || 20,
      global_6_10: markupSettings.global_6_10 || 20,
      global_10_15: markupSettings.global_10_15 || 20,
      global_15_20: markupSettings.global_15_20 || 20,
      global_20_plus: markupSettings.global_20_plus || 20
    });
  }, [markupSettings]);

  // Fetch order usage when order is selected
  const fetchOrderUsage = async (order) => {
    setSelectedOrder(order);
    setOrderUsage(null);
    setEditingIccid(false);
    setIccidError('');
    
    if (order.iccid) {
      setLoadingUsage(true);
      try {
        const response = await axios.get(`${API}/esim/usage/${order.iccid}`);
        if (response.data.success) {
          setOrderUsage(response.data.data);
        }
      } catch (err) {
        console.error('Error fetching usage:', err);
      } finally {
        setLoadingUsage(false);
      }
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleAddPromo = async (e) => {
    e.preventDefault();
    if (!newPromoCode) return;
    
    // Validate that we have at least one discount value
    if (!useSeparateDiscounts && !newPromoDiscount) return;
    if (useSeparateDiscounts && !newPromoLocalDiscount && !newPromoRegionalDiscount) return;

    setPromoLoading(true);
    try {
      const promoData = {
        code: newPromoCode.toUpperCase(),
        discount: useSeparateDiscounts 
          ? parseFloat(newPromoLocalDiscount || newPromoRegionalDiscount || 0) / 100 
          : parseFloat(newPromoDiscount) / 100,
        description: newPromoDesc || (useSeparateDiscounts 
          ? `${newPromoLocalDiscount || 0}% local / ${newPromoRegionalDiscount || 0}% regional`
          : `${newPromoDiscount}% discount`),
        validFor: promoValidFor,
        validCountries: promoValidFor === 'specific' ? promoCountries : [],
        maxUses: promoMaxUses ? parseInt(promoMaxUses) : null,
        maxUsesPerUser: promoMaxUsesPerUser ? parseInt(promoMaxUsesPerUser) : null,
        expiryDate: promoExpiryDate ? new Date(promoExpiryDate).toISOString() : null,
      };
      
      // Add separate discounts if enabled
      if (useSeparateDiscounts) {
        if (newPromoLocalDiscount) {
          promoData.localDiscount = parseFloat(newPromoLocalDiscount) / 100;
        }
        if (newPromoRegionalDiscount) {
          promoData.regionalDiscount = parseFloat(newPromoRegionalDiscount) / 100;
        }
      }
      
      await addPromoCode(promoData);
      setNewPromoCode('');
      setNewPromoDiscount('');
      setNewPromoLocalDiscount('');
      setNewPromoRegionalDiscount('');
      setUseSeparateDiscounts(false);
      setNewPromoDesc('');
      setPromoValidFor('all');
      setPromoCountries([]);
      setPromoMaxUses('');
      setPromoMaxUsesPerUser('');
      setPromoExpiryDate('');
      setShowPromoForm(false);
    } catch (err) {
      console.error('Error adding promo:', err);
    } finally {
      setPromoLoading(false);
    }
  };

  const handleUpdateMarkup = async () => {
    setMarkupLoading(true);
    try {
      await updateMarkupSettings(markupGrid);
    } catch (err) {
      console.error('Error updating markup:', err);
    } finally {
      setMarkupLoading(false);
    }
  };

  // Helper to update a single cell in the markup grid
  const updateMarkupCell = (key, value) => {
    setMarkupGrid(prev => ({
      ...prev,
      [key]: parseFloat(value) || 0
    }));
  };

  // Helper to set all values in a row (geography)
  const setRowValues = (geoType, value) => {
    const tiers = ['0_3', '3_6', '6_10', '10_15', '15_20', '20_plus'];
    const updates = {};
    tiers.forEach(tier => {
      updates[`${geoType}_${tier}`] = parseFloat(value) || 0;
    });
    setMarkupGrid(prev => ({ ...prev, ...updates }));
  };

  // Helper to set all values in a column (price tier)
  const setColumnValues = (tier, localVal, regionalVal, globalVal) => {
    setMarkupGrid(prev => ({
      ...prev,
      [`local_${tier}`]: parseFloat(localVal) || 0,
      [`regional_${tier}`]: parseFloat(regionalVal) || 0,
      [`global_${tier}`]: parseFloat(globalVal) || 0
    }));
  };

  const handleUpdateReferralSettings = async () => {
    setReferralSettingsLoading(true);
    try {
      await updateReferralSettings(referralSettings);
    } catch (err) {
      console.error('Error updating referral settings:', err);
    } finally {
      setReferralSettingsLoading(false);
    }
  };

  const handleAddCredits = async () => {
    if (!selectedUserForCredits || !creditsToAdd) return;
    
    try {
      await addUserCredits(selectedUserForCredits.email, parseFloat(creditsToAdd), creditsReason || 'Admin adjustment');
      // Refresh customers
      const usersData = await getAllUsers();
      setCustomers(usersData);
      setShowAddCreditsModal(false);
      setSelectedUserForCredits(null);
      setCreditsToAdd('');
      setCreditsReason('');
    } catch (err) {
      console.error('Error adding credits:', err);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    setDeletingUser(userToDelete.email);
    try {
      const response = await axios.delete(`${API}/admin/users/${encodeURIComponent(userToDelete.email)}`);
      if (response.data.success) {
        // Refresh customers and orders
        const [usersData, ordersData] = await Promise.all([
          getAllUsers(),
          getAllOrders()
        ]);
        setCustomers(usersData);
        setOrders(ordersData);
        setShowDeleteConfirm(false);
        setUserToDelete(null);
      }
    } catch (err) {
      console.error('Error deleting user:', err);
    } finally {
      setDeletingUser(null);
    }
  };

  const handleDeleteOrder = async () => {
    if (!orderToDelete) return;

    setDeletingOrder(orderToDelete.id);
    try {
      const response = await axios.delete(`${API}/admin/orders/${orderToDelete.id}`);
      if (response.data.success) {
        // Refresh orders list
        const ordersData = await getAllOrders();
        setOrders(ordersData);
        setShowDeleteOrderConfirm(false);
        setOrderToDelete(null);
      }
    } catch (err) {
      console.error('Error deleting order:', err);
    } finally {
      setDeletingOrder(null);
    }
  };

  const handleStartIccidEdit = () => {
    if (!selectedOrder) return;
    setIccidForm({
      iccid: selectedOrder.iccid || '',
      smdp_address: selectedOrder.smdp_address || '',
      matching_id: selectedOrder.matching_id || '',
    });
    setIccidError('');
    setEditingIccid(true);
  };

  const handleCancelIccidEdit = () => {
    setEditingIccid(false);
    setIccidError('');
  };

  const handleSaveIccid = async () => {
    if (!selectedOrder) return;
    const iccid = iccidForm.iccid.trim();
    if (!iccid) {
      setIccidError('ICCID is required');
      return;
    }
    if (iccid.length < 10 || !/^\d+$/.test(iccid)) {
      setIccidError('ICCID must be numeric and at least 10 digits');
      return;
    }
    setIccidSaving(true);
    setIccidError('');
    try {
      const response = await axios.patch(`${API}/admin/orders/${selectedOrder.id}/iccid`, {
        iccid,
        smdp_address: iccidForm.smdp_address.trim(),
        matching_id: iccidForm.matching_id.trim(),
      });
      if (response.data.success) {
        setSelectedOrder(response.data.order);
        // Refresh order list in background
        try {
          const ordersData = await getAllOrders();
          setOrders(ordersData);
        } catch (_) { /* non-blocking */ }
        // Re-fetch usage for new ICCID
        if (response.data.order?.iccid) {
          setOrderUsage(null);
          setLoadingUsage(true);
          try {
            const usageRes = await axios.get(`${API}/esim/usage/${response.data.order.iccid}`);
            if (usageRes.data.success) setOrderUsage(usageRes.data.data);
          } catch (_) { /* ignore */ }
          finally { setLoadingUsage(false); }
        }
        setEditingIccid(false);
      } else {
        setIccidError(response.data.message || 'Failed to update ICCID');
      }
    } catch (err) {
      setIccidError(err.response?.data?.detail || err.message || 'Failed to update ICCID');
    } finally {
      setIccidSaving(false);
    }
  };

  const filteredCountriesForPromo = allCountries.filter(c => 
    c.name.toLowerCase().includes(promoCountrySearch.toLowerCase()) &&
    !promoCountries.includes(c.id)
  ).slice(0, 8);

  const getValidForLabel = (promo) => {
    if (!promo.validFor || promo.validFor === 'all') return 'All Plans';
    if (promo.validFor === 'local') return 'Local Plans Only';
    if (promo.validFor === 'regional') return 'Regional Plans Only';
    if (promo.validFor === 'specific' && promo.validCountries?.length > 0) {
      return `${promo.validCountries.length} Countries`;
    }
    return 'All Plans';
  };

  // Blocked destinations functions
  const handleAddBlockedDestination = async () => {
    if (!newBlockedCode.trim()) return;
    try {
      const response = await axios.post(`${API}/admin/blocked-destinations`, {
        code: newBlockedCode.trim().toUpperCase(),
        name: newBlockedName.trim()
      });
      if (response.data.success) {
        setBlockedDestinations(response.data.blocked_destinations);
        setNewBlockedCode('');
        setNewBlockedName('');
      }
    } catch (err) {
      console.error('Error adding blocked destination:', err);
    }
  };

  const handleRemoveBlockedDestination = async (code) => {
    try {
      const response = await axios.delete(`${API}/admin/blocked-destinations/${code}`);
      if (response.data.success) {
        setBlockedDestinations(response.data.blocked_destinations);
      }
    } catch (err) {
      console.error('Error removing blocked destination:', err);
    }
  };

  // Marketing email functions
  const filteredUsersForMarketing = customers.filter(c => 
    c.email?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
    c.name?.toLowerCase().includes(userSearchTerm.toLowerCase())
  );

  const toggleUserSelection = (userEmail) => {
    setSelectedUsers(prev => 
      prev.includes(userEmail) 
        ? prev.filter(e => e !== userEmail)
        : [...prev, userEmail]
    );
  };

  const selectAllFilteredUsers = () => {
    const filteredEmails = filteredUsersForMarketing.map(u => u.email);
    setSelectedUsers(prev => {
      const allSelected = filteredEmails.every(e => prev.includes(e));
      if (allSelected) {
        return prev.filter(e => !filteredEmails.includes(e));
      }
      return [...new Set([...prev, ...filteredEmails])];
    });
  };

  const buildMarketingHtml = (content, images) => {
    const contentHtml = content.replace(/\n/g, '<br/>');
    const imagesHtml = images.filter(url => url.trim()).map(url =>
      `<tr><td style="padding:0 0 16px;"><img src="${url}" alt="" style="width:100%;max-width:100%;border-radius:10px;display:block;" /></td></tr>`
    ).join('');
    
    return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"></head>
<body style="margin:0;padding:0;background:#000000;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;">
<table width="100%" cellpadding="0" cellspacing="0" style="padding:32px 16px;background:#000000;">
<tr><td align="center">
<table width="520" cellpadding="0" cellspacing="0" style="max-width:520px;width:100%;background:#000000;border:2px solid #333333;border-radius:16px;overflow:hidden;">
<tr><td style="padding:32px 28px;">
<table width="100%" cellpadding="0" cellspacing="0">
  <tr><td style="padding:0 0 28px;text-align:center;">
    <img src="https://connectify-esims.com/connectify-logo-dark.png" alt="Connectify" style="height:36px;width:auto;" />
  </td></tr>
  ${imagesHtml}
  <tr><td style="padding:0 0 24px;">
    <div style="color:#e0e0e0;font-size:15px;line-height:1.8;">${contentHtml}</div>
  </td></tr>
  <tr><td style="text-align:center;padding:0 0 24px;">
    <a href="https://connectify-esims.com/destinations" style="display:inline-block;background:#ffffff;color:#000000;text-decoration:none;padding:12px 32px;border-radius:100px;font-weight:600;font-size:14px;">Shop Now</a>
  </td></tr>
  <tr><td><div style="height:1px;background:#222222;"></div></td></tr>
  <tr><td style="padding:16px 0 0;text-align:center;">
    <p style="margin:0 0 6px;color:#555555;font-size:11px;">Connectify eSIMs &middot; <a href="mailto:hello@connectify-esims.com" style="color:#555555;text-decoration:none;">hello@connectify-esims.com</a></p>
    <p style="margin:0;color:#444444;font-size:10px;"><a href="https://connectify-esims.com/unsubscribe" style="color:#555555;text-decoration:underline;">Unsubscribe</a></p>
  </td></tr>
</table>
</td></tr>
</table>
</td></tr></table>
</body></html>`;
  };

  const generateEmailPreview = () => {
    setEmailPreviewHtml(buildMarketingHtml(emailContent, emailImages));
    setShowEmailPreview(true);
  };

  const sendMarketingEmail = async () => {
    if (!emailSubject.trim() || !emailContent.trim()) {
      setEmailError('Please enter both subject and content');
      return;
    }
    
    const recipients = marketingRecipients === 'all' 
      ? customers.map(c => c.email).filter(Boolean)
      : selectedUsers;
    
    if (recipients.length === 0) {
      setEmailError('No recipients selected');
      return;
    }

    setSendingEmail(true);
    setEmailError('');
    setEmailSentSuccess(false);

    try {
      const response = await axios.post(`${API}/admin/send-marketing-email`, {
        recipients,
        subject: emailSubject,
        content: emailContent,
        images: emailImages.filter(url => url.trim())
      });
      
      if (response.data.success) {
        setEmailSentSuccess(true);
        setEmailSubject('');
        setEmailContent('');
        setEmailImages([]);
        setSelectedUsers([]);
        setTimeout(() => setEmailSentSuccess(false), 5000);
      } else {
        setEmailError(response.data.error || 'Failed to send emails');
      }
    } catch (err) {
      setEmailError(err.response?.data?.detail || 'Failed to send marketing email');
    } finally {
      setSendingEmail(false);
    }
  };

  // Get country name from code
  const getCountryNameFromCode = (code) => {
    const country = allCountries.find(c => c.id === code);
    return country ? country.name : code;
  };

  // Sort orders by date (newest first) and paginate
  const sortedOrders = [...orders].sort((a, b) => {
    const dateA = new Date(a.purchase_date || a.created_at || 0);
    const dateB = new Date(b.purchase_date || b.created_at || 0);
    return dateB - dateA; // Newest first
  });

  // Apply admin search filter (matches user_email OR the associated customer's
  // first/last name via the loaded `customers` list).
  const _adminMatchesSearch = (order, q) => {
    if (!q) return true;
    const query = q.toLowerCase();
    const email = (order.user_email || '').toLowerCase();
    if (email.includes(query)) return true;
    const cust = customers.find(c => c.email === order.user_email);
    if (cust) {
      const full = `${cust.first_name || cust.firstName || ''} ${cust.last_name || cust.lastName || ''}`.toLowerCase();
      if (full.includes(query)) return true;
    }
    return false;
  };
  const filteredOrders = sortedOrders.filter(o => _adminMatchesSearch(o, ordersSearch));

  const totalOrderPages = Math.ceil(filteredOrders.length / ordersPerPage);
  const paginatedOrders = filteredOrders.slice(
    (ordersPage - 1) * ordersPerPage,
    ordersPage * ordersPerPage
  );

  // --- Profits tab: filters + totals ---
  // Complimentary + manual-assigned eSIMs are explicitly excluded from the
  // revenue/profit maths (we didn't receive money for them and they shouldn't
  // skew analytics). They still render in the table at A$0 for transparency.
  const _isFreebie = (o) => !!(o.is_complimentary || o.is_manual_entry);
  const _asDate = (o) => new Date(o.purchase_date || o.created_at || 0);
  const _asMonth = (o) => {
    const d = _asDate(o);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  };

  const profitOrders = sortedOrders.filter((o) => {
    if (profitsFilterType === 'paid' && _isFreebie(o)) return false;
    if (profitsFilterType === 'complimentary' && !o.is_complimentary) return false;
    if (profitsFilterType === 'manual' && !o.is_manual_entry) return false;
    if (profitsFilterCountry !== 'all' && (o.country_id || o.country_code) !== profitsFilterCountry) return false;
    if (profitsFilterFrom) {
      const from = new Date(profitsFilterFrom);
      if (_asDate(o) < from) return false;
    }
    if (profitsFilterTo) {
      const to = new Date(profitsFilterTo);
      to.setHours(23, 59, 59, 999);
      if (_asDate(o) > to) return false;
    }
    if (profitsSearch && !_adminMatchesSearch(o, profitsSearch)) return false;
    return true;
  });

  const _effectiveRevenue = (o) => (_isFreebie(o) ? 0 : (o.amount_paid_aud || 0));
  const _effectiveProfit = (o) => (_isFreebie(o) ? 0 : (typeof o.profit_aud === 'number' ? o.profit_aud : 0));

  const totalProfitAud = profitOrders.reduce((s, o) => s + _effectiveProfit(o), 0);
  const totalRevenueAud = profitOrders.reduce((s, o) => s + _effectiveRevenue(o), 0);
  const paidCount = profitOrders.filter((o) => !_isFreebie(o)).length;
  const freebieCount = profitOrders.filter((o) => _isFreebie(o)).length;

  // Country options derived from the loaded orders.
  const profitCountryOptions = Array.from(
    new Map(sortedOrders.map((o) => [o.country_id || o.country_code, o.country_name])).entries()
  )
    .filter(([k]) => k)
    .sort((a, b) => (a[1] || '').localeCompare(b[1] || ''));

  if (!isAdmin) return null;

  const tabs = [
    { id: 'orders', label: 'Orders', icon: ShoppingCart, count: orders.length },
    { id: 'profits', label: 'Profits', icon: DollarSign, count: orders.length },
    { id: 'customers', label: 'Customers', icon: Users, count: customers.length },
    { id: 'traffic', label: 'Traffic', icon: BarChart3 },
    { id: 'promos', label: 'Promo Codes', icon: Tag, count: promoCodes.length },
    { id: 'referrals', label: 'Referrals', icon: Users },
    { id: 'marketing', label: 'Marketing', icon: Mail },
    { id: 'featured', label: 'Featured', icon: Globe },
    { id: 'grouping', label: 'Destination Grouping', icon: Layers },
    { id: 'assistant-kb', label: 'Assistant KB', icon: BookOpen },
    { id: 'names', label: 'Names', icon: Edit },
    { id: 'pricing', label: 'Pricing', icon: DollarSign },
    { id: 'blocked', label: 'Blocked', icon: Shield },
    { id: 'cache', label: 'Cache', icon: Package },
    { id: 'manual-esim', label: 'Manual eSIM', icon: Wrench },
    { id: 'blog', label: 'Blog', icon: FileText },
    { id: 'welcome-popup', label: 'Welcome Popup', icon: Sparkles },
    { id: 'welcome-back', label: 'Welcome Back', icon: MapPin }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="pt-28 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 text-gray-600 hover:text-black mb-2"
              >
                <ArrowLeft size={18} /> Back to Dashboard
              </button>
              <h1 className="text-3xl sm:text-4xl font-black text-gray-900">Admin Dashboard</h1>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-3 rounded-xl font-bold whitespace-nowrap transition-colors ${
                  activeTab === tab.id 
                    ? 'bg-black text-white' 
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                <tab.icon size={18} />
                {tab.label}
                {tab.count !== undefined && (
                  <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                    activeTab === tab.id ? 'bg-white/20' : 'bg-gray-100'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Content */}
          {loadingData ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : (
            <>
              {/* Orders Tab */}
              {activeTab === 'orders' && (
                <div className="space-y-4">
                  <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                    <div className="p-4 border-b border-gray-100">
                      <div className="relative max-w-md">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          value={ordersSearch}
                          onChange={(e) => { setOrdersSearch(e.target.value); setOrdersPage(1); }}
                          placeholder="Search orders by customer name or email…"
                          className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:border-black"
                          data-testid="orders-search"
                        />
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-100">
                          <tr>
                            <th className="text-left px-6 py-4 text-sm font-bold text-gray-500">Order ID</th>
                            <th className="text-left px-6 py-4 text-sm font-bold text-gray-500">Customer</th>
                            <th className="text-left px-6 py-4 text-sm font-bold text-gray-500">Destination</th>
                            <th className="text-left px-6 py-4 text-sm font-bold text-gray-500">Plan</th>
                            <th className="text-left px-6 py-4 text-sm font-bold text-gray-500">Paid (AUD)</th>
                            <th className="text-left px-6 py-4 text-sm font-bold text-gray-500">Status</th>
                            <th className="text-left px-6 py-4 text-sm font-bold text-gray-500">Date</th>
                            <th className="text-left px-6 py-4 text-sm font-bold text-gray-500">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {paginatedOrders.length === 0 ? (
                            <tr>
                              <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                                No orders yet
                              </td>
                            </tr>
                          ) : (
                            paginatedOrders.map((order) => (
                            <tr key={order.id || order._id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 font-mono text-sm text-gray-600">{order.id?.substring(0, 8)}...</td>
                              <td className="px-6 py-4 text-gray-900">{order.user_email}</td>
                              <td className="px-6 py-4 font-semibold text-gray-900">{order.country_name}</td>
                              <td className="px-6 py-4 text-gray-600">{order.plan_name}</td>
                              <td className="px-6 py-4 font-semibold text-gray-900">
                                {order.amount_paid_aud ? `A$${order.amount_paid_aud.toFixed(2)}` : order.payment_session_id?.startsWith('credits-') ? 'Credits' : '—'}
                              </td>
                              <td className="px-6 py-4">
                                <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                                  (() => {
                                    const s = order.roamify_status || order.status;
                                    if (s === 'INSTALLED' || s === 'ACTIVE') return 'bg-green-100 text-green-800';
                                    if (s === 'READY' || s === 'PENDING' || s === 'NEW') return 'bg-yellow-100 text-yellow-800';
                                    if (s === 'EXPIRED') return 'bg-gray-100 text-gray-600';
                                    if (s === 'REVOKED') return 'bg-red-100 text-red-800';
                                    return 'bg-gray-100 text-gray-600';
                                  })()
                                }`}>
                                  {order.roamify_status || order.status}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-gray-600">{new Date(order.purchase_date).toLocaleDateString()}</td>
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => fetchOrderUsage(order)}
                                    className="flex items-center gap-1 px-3 py-2 bg-emerald-100 text-emerald-700 rounded-lg font-semibold text-sm hover:bg-emerald-200 transition-colors"
                                    data-testid={`view-order-${order.id}`}
                                  >
                                    <Eye size={16} /> View
                                  </button>
                                  <button
                                    onClick={() => {
                                      setOrderToDelete(order);
                                      setShowDeleteOrderConfirm(true);
                                    }}
                                    className="flex items-center gap-1 px-3 py-2 bg-red-100 text-red-700 rounded-lg font-semibold text-sm hover:bg-red-200 transition-colors"
                                    data-testid={`delete-order-${order.id}`}
                                  >
                                    <Trash2 size={16} /> Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  </div>
                  
                  {/* Pagination Controls */}
                  {totalOrderPages > 1 && (
                    <div className="flex items-center justify-between bg-white rounded-2xl border border-gray-100 px-6 py-4">
                      <div className="text-sm text-gray-600">
                        Showing {((ordersPage - 1) * ordersPerPage) + 1} - {Math.min(ordersPage * ordersPerPage, filteredOrders.length)} of {filteredOrders.length} orders
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setOrdersPage(1)}
                          disabled={ordersPage === 1}
                          className="px-3 py-2 rounded-lg text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100 hover:bg-gray-200 text-gray-700"
                        >
                          First
                        </button>
                        <button
                          onClick={() => setOrdersPage(p => Math.max(1, p - 1))}
                          disabled={ordersPage === 1}
                          className="px-3 py-2 rounded-lg text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100 hover:bg-gray-200 text-gray-700"
                        >
                          <ChevronLeft size={18} />
                        </button>
                        
                        <div className="flex items-center gap-1">
                          {[...Array(Math.min(5, totalOrderPages))].map((_, idx) => {
                            let pageNum;
                            if (totalOrderPages <= 5) {
                              pageNum = idx + 1;
                            } else if (ordersPage <= 3) {
                              pageNum = idx + 1;
                            } else if (ordersPage >= totalOrderPages - 2) {
                              pageNum = totalOrderPages - 4 + idx;
                            } else {
                              pageNum = ordersPage - 2 + idx;
                            }
                            return (
                              <button
                                key={pageNum}
                                onClick={() => setOrdersPage(pageNum)}
                                className={`w-10 h-10 rounded-lg text-sm font-bold transition-colors ${
                                  ordersPage === pageNum
                                    ? 'bg-black text-white'
                                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                }`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                        </div>
                        
                        <button
                          onClick={() => setOrdersPage(p => Math.min(totalOrderPages, p + 1))}
                          disabled={ordersPage === totalOrderPages}
                          className="px-3 py-2 rounded-lg text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100 hover:bg-gray-200 text-gray-700"
                        >
                          <ChevronRight size={18} />
                        </button>
                        <button
                          onClick={() => setOrdersPage(totalOrderPages)}
                          disabled={ordersPage === totalOrderPages}
                          className="px-3 py-2 rounded-lg text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100 hover:bg-gray-200 text-gray-700"
                        >
                          Last
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Profits Tab */}
              {activeTab === 'profits' && (
                <div className="space-y-4" data-testid="profits-tab">
                  {/* Filters */}
                  <div className="bg-white rounded-2xl border border-gray-100 p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Type</label>
                      <select
                        value={profitsFilterType}
                        onChange={(e) => setProfitsFilterType(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-sm font-medium focus:border-black outline-none"
                        data-testid="profits-filter-type"
                      >
                        <option value="all">All orders</option>
                        <option value="paid">Paid only</option>
                        <option value="complimentary">Complimentary only</option>
                        <option value="manual">Manual eSIMs only</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Destination</label>
                      <select
                        value={profitsFilterCountry}
                        onChange={(e) => setProfitsFilterCountry(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-sm font-medium focus:border-black outline-none"
                        data-testid="profits-filter-country"
                      >
                        <option value="all">All destinations</option>
                        {profitCountryOptions.map(([code, name]) => (
                          <option key={code} value={code}>{name || code}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">From</label>
                      <input
                        type="date"
                        value={profitsFilterFrom}
                        onChange={(e) => setProfitsFilterFrom(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-sm font-medium focus:border-black outline-none"
                        data-testid="profits-filter-from"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">To</label>
                      <input
                        type="date"
                        value={profitsFilterTo}
                        onChange={(e) => setProfitsFilterTo(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-sm font-medium focus:border-black outline-none"
                        data-testid="profits-filter-to"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Search</label>
                      <input
                        type="text"
                        value={profitsSearch}
                        onChange={(e) => setProfitsSearch(e.target.value)}
                        placeholder="Email or name"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-sm font-medium focus:border-black outline-none"
                        data-testid="profits-filter-search"
                      />
                    </div>
                    {(profitsFilterType !== 'all' || profitsFilterCountry !== 'all' || profitsFilterFrom || profitsFilterTo || profitsSearch) && (
                      <div className="lg:col-span-5 flex items-center justify-end">
                        <button
                          onClick={() => { setProfitsFilterType('all'); setProfitsFilterCountry('all'); setProfitsFilterFrom(''); setProfitsFilterTo(''); setProfitsSearch(''); }}
                          className="text-xs font-bold text-gray-500 hover:text-black underline underline-offset-2"
                          data-testid="profits-clear-filters"
                        >
                          Clear all filters
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Summary cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                    <div className="bg-white rounded-2xl border border-gray-100 p-6">
                      <div className="text-sm font-semibold text-gray-500 mb-1">Revenue (paid)</div>
                      <div className="text-3xl font-black text-gray-900" data-testid="profits-total-revenue">
                        A${totalRevenueAud.toFixed(2)}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">Excludes complimentary &amp; manual</div>
                    </div>
                    <div className="bg-white rounded-2xl border border-gray-100 p-6">
                      <div className="text-sm font-semibold text-gray-500 mb-1">Profit</div>
                      <div
                        className={`text-3xl font-black ${totalProfitAud >= 0 ? 'text-emerald-600' : 'text-red-600'}`}
                        data-testid="profits-total-profit"
                      >
                        {totalProfitAud < 0 ? '-' : ''}A${Math.abs(totalProfitAud).toFixed(2)}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        {totalRevenueAud > 0 ? `${((totalProfitAud / totalRevenueAud) * 100).toFixed(1)}% margin` : '—'}
                      </div>
                    </div>
                    <div className="bg-white rounded-2xl border border-gray-100 p-6">
                      <div className="text-sm font-semibold text-gray-500 mb-1">Paid orders</div>
                      <div className="text-3xl font-black text-gray-900" data-testid="profits-paid-count">{paidCount}</div>
                      <div className="text-xs text-gray-400 mt-1">
                        Avg A${paidCount > 0 ? (totalRevenueAud / paidCount).toFixed(2) : '0.00'}
                      </div>
                    </div>
                    <div className="bg-white rounded-2xl border border-gray-100 p-6">
                      <div className="text-sm font-semibold text-gray-500 mb-1">Free / manual</div>
                      <div className="text-3xl font-black text-gray-900" data-testid="profits-freebie-count">{freebieCount}</div>
                      <div className="text-xs text-gray-400 mt-1">Not counted in revenue</div>
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-100 sticky top-0 z-10">
                          <tr>
                            <th className="text-left px-6 py-4 text-sm font-bold text-gray-500">Order #</th>
                            <th className="text-left px-6 py-4 text-sm font-bold text-gray-500">Date</th>
                            <th className="text-left px-6 py-4 text-sm font-bold text-gray-500">Destination</th>
                            <th className="text-left px-6 py-4 text-sm font-bold text-gray-500">Package</th>
                            <th className="text-left px-6 py-4 text-sm font-bold text-gray-500">Price Paid</th>
                            <th className="text-left px-6 py-4 text-sm font-bold text-gray-500">Profit</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {loadingData ? (
                            <tr><td colSpan={6} className="px-6 py-12 text-center text-gray-500">Loading…</td></tr>
                          ) : profitOrders.length === 0 ? (
                            <tr><td colSpan={6} className="px-6 py-12 text-center text-gray-500">No orders match the current filters</td></tr>
                          ) : (
                            profitOrders.map((order) => {
                              const freebie = _isFreebie(order);
                              const effProfit = _effectiveProfit(order);
                              return (
                              <tr key={order.id || order._id} className="hover:bg-gray-50" data-testid={`profit-row-${order.id}`}>
                                <td className="px-6 py-4 font-mono text-sm text-gray-600">
                                  {order.id?.substring(0, 8)}...
                                  {order.is_complimentary && <span className="ml-2 text-[10px] font-bold text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">COMP</span>}
                                  {order.is_manual_entry && <span className="ml-2 text-[10px] font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">MANUAL</span>}
                                </td>
                                <td className="px-6 py-4 text-gray-600">{new Date(order.purchase_date || order.created_at).toLocaleDateString()}</td>
                                <td className="px-6 py-4 font-semibold text-gray-900">{order.country_name}</td>
                                <td className="px-6 py-4 text-gray-600">{order.plan_name}</td>
                                <td className="px-6 py-4 font-semibold text-gray-900">
                                  {freebie ? <span className="text-gray-400">A$0.00</span>
                                    : order.amount_paid_aud ? `A$${order.amount_paid_aud.toFixed(2)}`
                                    : order.payment_session_id?.startsWith('credits-') ? 'Credits' : '—'}
                                </td>
                                <td
                                  className={`px-6 py-4 font-semibold ${
                                    freebie
                                      ? 'text-gray-400'
                                      : effProfit >= 0
                                        ? 'text-emerald-600'
                                        : 'text-red-600'
                                  }`}
                                  title={
                                    freebie
                                      ? 'Complimentary / manual eSIM — not counted toward profit'
                                      : order.base_cost_aud != null
                                        ? `Paid A$${(order.amount_paid_aud || 0).toFixed(2)} − Roamify cost A$${order.base_cost_aud.toFixed(2)} − $0.20 fee − 1.6% processor`
                                        : 'Base package cost unavailable'
                                  }
                                >
                                  {freebie
                                    ? 'A$0.00'
                                    : order.profit_aud == null
                                      ? '—'
                                      : `${effProfit < 0 ? '-' : ''}A$${Math.abs(effProfit).toFixed(2)}`}
                                </td>
                              </tr>
                            );})
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Customers Tab */}
              {activeTab === 'customers' && (
                <div className="space-y-6">
                  {/* Add User Button / Form */}
                  <div className="flex justify-between items-center">
                    <h2 className="text-xl font-bold text-gray-900">Customers</h2>
                    <button
                      onClick={() => { setShowAddUser(!showAddUser); setAddUserError(''); }}
                      className={`px-4 py-2 rounded-xl font-bold text-sm transition-colors ${showAddUser ? 'bg-gray-200 text-gray-700' : 'bg-black text-white hover:bg-gray-800'}`}
                    >
                      {showAddUser ? 'Cancel' : '+ Add User'}
                    </button>
                  </div>

                  {showAddUser && (
                    <div className="bg-white rounded-2xl border border-gray-100 p-6">
                      <h3 className="font-bold text-gray-900 mb-4">Create New User</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                          <input
                            type="text"
                            value={newUser.firstName}
                            onChange={(e) => setNewUser({ ...newUser, firstName: e.target.value })}
                            placeholder="John"
                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                            data-testid="add-user-firstname"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                          <input
                            type="text"
                            value={newUser.lastName}
                            onChange={(e) => setNewUser({ ...newUser, lastName: e.target.value })}
                            placeholder="Smith"
                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                            data-testid="add-user-lastname"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                          <input
                            type="email"
                            value={newUser.email}
                            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                            placeholder="john@example.com"
                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                            data-testid="add-user-email"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                          <input
                            type="text"
                            value={newUser.password}
                            onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                            placeholder="Temporary password"
                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                            data-testid="add-user-password"
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-3 mb-4">
                        <input
                          type="checkbox"
                          id="sendWelcome"
                          checked={newUser.sendWelcomeEmail}
                          onChange={(e) => setNewUser({ ...newUser, sendWelcomeEmail: e.target.checked })}
                          className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                          data-testid="add-user-welcome-email"
                        />
                        <label htmlFor="sendWelcome" className="text-sm text-gray-700">Send welcome email with WELCOME20 promo code</label>
                      </div>
                      {addUserError && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm">{addUserError}</div>
                      )}
                      <button
                        onClick={async () => {
                          if (!newUser.firstName || !newUser.lastName || !newUser.email || !newUser.password) {
                            setAddUserError('All fields are required');
                            return;
                          }
                          setAddingUser(true);
                          setAddUserError('');
                          try {
                            const resp = await axios.post(`${API}/admin/create-user`, newUser);
                            if (resp.data.success) {
                              setShowAddUser(false);
                              setNewUser({ firstName: '', lastName: '', email: '', password: '', sendWelcomeEmail: true });
                              // Refresh customer list
                              const usersResp = await getAllUsers();
                              if (usersResp) setCustomers(usersResp);
                            }
                          } catch (err) {
                            setAddUserError(err.response?.data?.detail || 'Failed to create user');
                          } finally {
                            setAddingUser(false);
                          }
                        }}
                        disabled={addingUser}
                        className="px-6 py-2.5 bg-black text-white rounded-xl font-bold text-sm hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2"
                        data-testid="add-user-submit"
                      >
                        {addingUser ? <><Loader2 size={16} className="animate-spin" /> Creating...</> : 'Create User'}
                      </button>
                    </div>
                  )}

                  <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                  <div className="p-4 border-b border-gray-100">
                    <div className="relative max-w-md">
                      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={customersSearch}
                        onChange={(e) => setCustomersSearch(e.target.value)}
                        placeholder="Search customers by name or email…"
                        className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:border-black"
                        data-testid="customers-search"
                      />
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>
                          <th className="text-left px-6 py-4 text-sm font-bold text-gray-500">Name</th>
                          <th className="text-left px-6 py-4 text-sm font-bold text-gray-500">Email</th>
                          <th className="text-left px-6 py-4 text-sm font-bold text-gray-500">Orders</th>
                          <th className="text-left px-6 py-4 text-sm font-bold text-gray-500">Joined</th>
                          <th className="text-left px-6 py-4 text-sm font-bold text-gray-500">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {customers.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                              No customers yet
                            </td>
                          </tr>
                        ) : (
                          [...customers].filter((c) => {
                            if (!customersSearch) return true;
                            const q = customersSearch.toLowerCase();
                            const email = (c.email || '').toLowerCase();
                            const name = (c.name || `${c.first_name || c.firstName || ''} ${c.last_name || c.lastName || ''}`).toLowerCase();
                            return email.includes(q) || name.includes(q);
                          }).sort((a, b) => {
                            // Sort by created_at or joinDate, newest first
                            const dateA = new Date(a.created_at || a.joinDate || 0);
                            const dateB = new Date(b.created_at || b.joinDate || 0);
                            return dateB - dateA;
                          }).map((customer) => {
                            const customerOrders = orders.filter(o => o.user_email === customer.email);
                            return (
                              <tr key={customer.email} className="hover:bg-gray-50">
                                <td className="px-6 py-4 font-semibold text-gray-900">{customer.name}</td>
                                <td className="px-6 py-4 text-gray-600">{customer.email}</td>
                                <td className="px-6 py-4">
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => setViewOrdersUser(customer)}
                                      className="px-3 py-1 bg-gray-100 rounded-full text-sm font-semibold hover:bg-emerald-100 hover:text-emerald-700 transition-colors cursor-pointer"
                                      data-testid={`view-orders-${customer.email}`}
                                    >
                                      {customerOrders.length} orders
                                    </button>
                                    <button
                                      onClick={() => setComplimentaryEsimUser(customer)}
                                      className="w-7 h-7 flex items-center justify-center rounded-full bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"
                                      title="Add complimentary eSIM for this user"
                                      data-testid={`add-comp-esim-${customer.email}`}
                                    >
                                      <Plus size={14} />
                                    </button>
                                  </div>
                                </td>
                                <td className="px-6 py-4 text-gray-600">{customer.joinDate || customer.firstPurchaseDate || '-'}</td>
                                <td className="px-6 py-4">
                                  <button
                                    onClick={() => {
                                      setUserToDelete(customer);
                                      setShowDeleteConfirm(true);
                                    }}
                                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Delete user and all their data"
                                    data-testid={`delete-user-${customer.email}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* User Orders Modal */}
                  {viewOrdersUser && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="user-orders-modal">
                      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setViewOrdersUser(null)} />
                      <div className="relative bg-white rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden shadow-2xl">
                        <div className="flex items-center justify-between p-5 border-b border-gray-100">
                          <div>
                            <h3 className="font-bold text-gray-900">{viewOrdersUser.name || viewOrdersUser.email}</h3>
                            <p className="text-sm text-gray-500">{viewOrdersUser.email}</p>
                          </div>
                          <button onClick={() => setViewOrdersUser(null)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg">
                            <X size={20} />
                          </button>
                        </div>
                        <div className="overflow-y-auto max-h-[calc(80vh-80px)] p-5">
                          {(() => {
                            const userOrders = orders.filter(o => o.user_email === viewOrdersUser.email).sort((a, b) => new Date(b.purchase_date || 0) - new Date(a.purchase_date || 0));
                            if (userOrders.length === 0) {
                              return <p className="text-center text-gray-400 py-8">No orders found</p>;
                            }
                            return (
                              <div className="space-y-3">
                                {userOrders.map((order) => (
                                  <div key={order.id} className="border border-gray-100 rounded-xl p-4 hover:bg-gray-50 transition-colors">
                                    <div className="flex items-start justify-between mb-2">
                                      <div>
                                        <p className="font-bold text-gray-900">{order.country_name || 'N/A'}</p>
                                        <p className="text-sm text-gray-500">{order.plan_name || 'N/A'}</p>
                                      </div>
                                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                                        order.status === 'ACTIVE' ? 'bg-emerald-100 text-emerald-700' :
                                        order.status === 'COMPLETED' ? 'bg-blue-100 text-blue-700' :
                                        order.status === 'EXPIRED' ? 'bg-gray-100 text-gray-600' :
                                        'bg-amber-100 text-amber-700'
                                      }`}>
                                        {order.status || 'PENDING'}
                                      </span>
                                    </div>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                                      <div>
                                        <p className="text-gray-400">Amount</p>
                                        <p className="font-semibold text-gray-900">A${(order.price || order.amount_paid_aud || 0).toFixed(2)}</p>
                                      </div>
                                      <div>
                                        <p className="text-gray-400">Data</p>
                                        <p className="font-semibold text-gray-900">{order.is_unlimited ? 'Unlimited' : `${order.data_amount || '?'} GB`}</p>
                                      </div>
                                      <div>
                                        <p className="text-gray-400">Duration</p>
                                        <p className="font-semibold text-gray-900">{order.duration_days || '?'} days</p>
                                      </div>
                                      <div>
                                        <p className="text-gray-400">Date</p>
                                        <p className="font-semibold text-gray-900">{order.purchase_date ? order.purchase_date.split('T')[0] : '-'}</p>
                                      </div>
                                    </div>
                                    {order.iccid && (
                                      <p className="text-xs text-gray-400 mt-2 font-mono">ICCID: {order.iccid}</p>
                                    )}
                                    {order.promo_code_used && (
                                      <p className="text-xs text-emerald-600 mt-1 font-medium">Promo: {order.promo_code_used}</p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  )}

                </div>
                </div>
              )}

              {/* Traffic Analytics Tab */}
              {activeTab === 'traffic' && (
                <div className="space-y-6">
                  {/* Period Selector with Custom Date Range */}
                  <div className="bg-white rounded-2xl p-6 border border-gray-100">
                    <div className="flex flex-col gap-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div>
                          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                            <TrendingUp className="text-emerald-600" />
                            Traffic Analytics
                          </h2>
                          <p className="text-gray-500 text-sm mt-1">Track which destinations users are browsing</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {['day', 'week', 'month', 'year', 'custom'].map(period => (
                            <button
                              key={period}
                              onClick={() => setTrafficPeriod(period)}
                              className={`px-4 py-2 rounded-xl font-semibold text-sm transition-colors ${
                                trafficPeriod === period
                                  ? 'bg-black text-white'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              {period.charAt(0).toUpperCase() + period.slice(1)}
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      {/* Custom Date Range Picker */}
                      {trafficPeriod === 'custom' && (
                        <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-gray-100">
                          <Calendar className="text-gray-400" size={20} />
                          <div className="flex items-center gap-2">
                            <label className="text-sm text-gray-600">From:</label>
                            <input
                              type="date"
                              value={customStartDate}
                              onChange={(e) => setCustomStartDate(e.target.value)}
                              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <label className="text-sm text-gray-600">To:</label>
                            <input
                              type="date"
                              value={customEndDate}
                              onChange={(e) => setCustomEndDate(e.target.value)}
                              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
                            />
                          </div>
                          {customStartDate && customEndDate && (
                            <span className="text-xs text-gray-500">
                              ({Math.ceil((new Date(customEndDate) - new Date(customStartDate)) / (1000 * 60 * 60 * 24)) + 1} days)
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {loadingTraffic ? (
                    <div className="flex justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                    </div>
                  ) : trafficData ? (
                    <>
                      {/* Stats Overview */}
                      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                        <div className="bg-white rounded-2xl p-5 border border-gray-100">
                          <p className="text-sm text-gray-500 mb-1">Total Page Views</p>
                          <p className="text-3xl font-black text-gray-900">{trafficData.total_views?.toLocaleString() || 0}</p>
                        </div>
                        <div className="bg-white rounded-2xl p-5 border border-gray-100">
                          <p className="text-sm text-gray-500 mb-1">Unique Sessions</p>
                          <p className="text-3xl font-black text-indigo-600">{trafficData.unique_sessions?.toLocaleString() || 0}</p>
                        </div>
                        <div className="bg-white rounded-2xl p-5 border border-gray-100">
                          <p className="text-sm text-gray-500 mb-1">Countries Viewed</p>
                          <p className="text-3xl font-black text-blue-600">{trafficData.by_country?.length || 0}</p>
                        </div>
                        <div className="bg-white rounded-2xl p-5 border border-gray-100">
                          <p className="text-sm text-gray-500 mb-1">Top Destination</p>
                          <p className="text-xl font-bold text-emerald-600 truncate">
                            {trafficData.by_country?.[0]?.country_name || '—'}
                          </p>
                        </div>
                        <div className="bg-white rounded-2xl p-5 border border-gray-100">
                          <p className="text-sm text-gray-500 mb-1">Top Region</p>
                          <p className="text-xl font-bold text-purple-600">
                            {trafficData.by_region?.[0]?.region || '—'}
                          </p>
                        </div>
                      </div>

                      {/* Page Traffic Breakdown */}
                      {trafficData.by_page?.length > 0 && (
                        <div className="bg-white rounded-2xl p-6 border border-gray-100">
                          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <Eye size={18} />
                            Page Traffic Breakdown
                          </h3>
                          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                            {trafficData.by_page.map((page) => (
                              <div key={page.page_type || page._id} className="bg-gray-50 rounded-xl p-4 text-center">
                                <p className="text-2xl font-black text-gray-900">{page.views?.toLocaleString()}</p>
                                <p className="text-xs text-gray-500 capitalize mt-1">{page.page_type?.replace('_', ' ') || 'Unknown'}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Fraud Monitoring Section */}
                      <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl p-6 border border-red-100">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-bold text-gray-900 flex items-center gap-2">
                            <Shield className="text-red-600" size={20} />
                            Fraud Monitoring
                          </h3>
                          {fraudData?.total_blocked > 0 && (
                            <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-semibold">
                              {fraudData.total_blocked} blocked
                            </span>
                          )}
                        </div>
                        
                        {loadingFraud ? (
                          <div className="flex justify-center py-4">
                            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                          </div>
                        ) : fraudData ? (
                          <div className="grid sm:grid-cols-3 gap-4">
                            <div className="bg-white rounded-xl p-4">
                              <p className="text-sm text-gray-500">Attempts Blocked</p>
                              <p className="text-2xl font-black text-red-600">{fraudData.total_blocked || 0}</p>
                            </div>
                            <div className="bg-white rounded-xl p-4">
                              <p className="text-sm text-gray-500">Amount Saved</p>
                              <p className="text-2xl font-black text-emerald-600">${fraudData.total_amount_saved?.toLocaleString() || 0}</p>
                            </div>
                            <div className="bg-white rounded-xl p-4">
                              <p className="text-sm text-gray-500">Repeat Offenders</p>
                              <p className="text-2xl font-black text-orange-600">{fraudData.repeat_offenders?.length || 0}</p>
                            </div>
                          </div>
                        ) : (
                          <p className="text-gray-500 text-center py-4">No fraud data available</p>
                        )}
                        
                        {/* Recent Fraud Attempts */}
                        {fraudData?.attempts?.length > 0 && (
                          <div className="mt-4">
                            <h4 className="font-semibold text-gray-700 mb-2 text-sm">Recent Blocked Attempts</h4>
                            <div className="bg-white rounded-xl overflow-hidden max-h-48 overflow-y-auto">
                              <table className="w-full text-sm">
                                <thead className="bg-gray-50 sticky top-0">
                                  <tr>
                                    <th className="text-left px-3 py-2 text-xs font-bold text-gray-500">Time</th>
                                    <th className="text-left px-3 py-2 text-xs font-bold text-gray-500">User</th>
                                    <th className="text-left px-3 py-2 text-xs font-bold text-gray-500">Package</th>
                                    <th className="text-right px-3 py-2 text-xs font-bold text-gray-500">Expected</th>
                                    <th className="text-right px-3 py-2 text-xs font-bold text-gray-500">Paid</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {fraudData.attempts.slice(0, 10).map((attempt) => (
                                    <tr key={attempt.timestamp + attempt.user_email} className="border-b border-gray-50">
                                      <td className="px-3 py-2 text-gray-500">
                                        {new Date(attempt.timestamp).toLocaleString('en-AU', { 
                                          month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
                                        })}
                                      </td>
                                      <td className="px-3 py-2 font-medium text-gray-900 truncate max-w-[150px]">
                                        {attempt.user_email}
                                      </td>
                                      <td className="px-3 py-2 text-gray-600 truncate max-w-[150px]">
                                        {attempt.plan_name || attempt.country_name}
                                      </td>
                                      <td className="px-3 py-2 text-right text-emerald-600 font-semibold">
                                        ${attempt.expected_min?.toFixed(2)}
                                      </td>
                                      <td className="px-3 py-2 text-right text-red-600 font-semibold">
                                        ${attempt.amount_paid?.toFixed(2)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                        
                        {/* Repeat Offenders */}
                        {fraudData?.repeat_offenders?.length > 0 && (
                          <div className="mt-4">
                            <h4 className="font-semibold text-gray-700 mb-2 text-sm flex items-center gap-2">
                              <AlertTriangle className="text-orange-500" size={16} />
                              Repeat Offenders
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {fraudData.repeat_offenders.map((offender) => (
                                <span key={offender.email} className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold">
                                  {offender.email} ({offender.attempts}x)
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Traffic by Day Chart */}
                      {trafficData.by_day?.length > 0 && (
                        <div className="bg-white rounded-2xl p-6 border border-gray-100">
                          <h3 className="font-bold text-gray-900 mb-4">Daily Traffic</h3>
                          <div className="h-40 flex items-end gap-1">
                            {trafficData.by_day.map((day) => {
                              const maxViews = Math.max(...trafficData.by_day.map(d => d.views));
                              const height = maxViews > 0 ? (day.views / maxViews) * 100 : 0;
                              return (
                                <div key={day.date} className="flex-1 flex flex-col items-center group">
                                  <div className="relative w-full">
                                    <div
                                      className="w-full bg-emerald-500 rounded-t-sm transition-all hover:bg-emerald-600"
                                      style={{ height: `${Math.max(height, 2)}px` }}
                                    />
                                    <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                      {day.views} views
                                    </div>
                                  </div>
                                  <span className="text-xs text-gray-400 mt-1 transform -rotate-45 origin-left">
                                    {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      <div className="grid lg:grid-cols-2 gap-6">
                        {/* Top Countries */}
                        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                          <div className="p-4 border-b border-gray-100 bg-gray-50">
                            <h3 className="font-bold text-gray-900 flex items-center gap-2">
                              <Globe size={18} className="text-blue-600" />
                              Top Destinations
                            </h3>
                          </div>
                          <div className="max-h-96 overflow-y-auto">
                            {trafficData.by_country?.length > 0 ? (
                              <table className="w-full">
                                <thead className="bg-gray-50 sticky top-0">
                                  <tr>
                                    <th className="text-left px-4 py-2 text-xs font-bold text-gray-500">#</th>
                                    <th className="text-left px-4 py-2 text-xs font-bold text-gray-500">Destination</th>
                                    <th className="text-left px-4 py-2 text-xs font-bold text-gray-500">Type</th>
                                    <th className="text-right px-4 py-2 text-xs font-bold text-gray-500">Views</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {trafficData.by_country.map((item, idx) => (
                                    <tr key={item.country_id || item.country_name} className="border-b border-gray-50 hover:bg-gray-50">
                                      <td className="px-4 py-3 text-sm text-gray-400">{idx + 1}</td>
                                      <td className="px-4 py-3">
                                        <span className="font-semibold text-gray-900">{item.country_name}</span>
                                      </td>
                                      <td className="px-4 py-3">
                                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                          item.is_region 
                                            ? 'bg-purple-100 text-purple-700' 
                                            : 'bg-blue-100 text-blue-700'
                                        }`}>
                                          {item.is_region ? 'Regional' : 'Local'}
                                        </span>
                                      </td>
                                      <td className="px-4 py-3 text-right font-bold text-gray-900">
                                        {item.views.toLocaleString()}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <div className="py-12 text-center text-gray-500">
                                No traffic data yet
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Traffic by Region */}
                        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                          <div className="p-4 border-b border-gray-100 bg-gray-50">
                            <h3 className="font-bold text-gray-900 flex items-center gap-2">
                              <MapPin size={18} className="text-purple-600" />
                              Traffic by Region
                            </h3>
                          </div>
                          <div className="p-4 space-y-3">
                            {trafficData.by_region?.length > 0 ? (
                              trafficData.by_region.map((item) => {
                                const maxViews = Math.max(...trafficData.by_region.map(r => r.views));
                                const percentage = maxViews > 0 ? (item.views / maxViews) * 100 : 0;
                                const totalViews = trafficData.total_views || 1;
                                const sharePercentage = ((item.views / totalViews) * 100).toFixed(1);
                                return (
                                  <div key={item.region}>
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="font-semibold text-gray-900">{item.region}</span>
                                      <span className="text-sm text-gray-600">
                                        {item.views.toLocaleString()} ({sharePercentage}%)
                                      </span>
                                    </div>
                                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                      <div 
                                        className="h-full bg-gradient-to-r from-purple-500 to-purple-600 rounded-full transition-all"
                                        style={{ width: `${percentage}%` }}
                                      />
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <div className="py-8 text-center text-gray-500">
                                No region data yet
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Date Range Info */}
                      <div className="text-center text-sm text-gray-500">
                        Showing data from {trafficData.start_date} to {trafficData.end_date}
                      </div>
                    </>
                  ) : (
                    <div className="bg-white rounded-2xl p-12 border border-gray-100 text-center">
                      <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No traffic data available yet</p>
                      <p className="text-gray-400 text-sm mt-1">Traffic will be tracked as users browse destinations</p>
                    </div>
                  )}
                </div>
              )}

              {/* Promo Codes Tab */}
              {activeTab === 'promos' && (
                <div className="space-y-6">
                  {/* Promo Analytics Section */}
                  {promoAnalytics && (
                    <div className="space-y-4" data-testid="promo-analytics">
                      <h2 className="text-xl font-bold text-gray-900">Promo Analytics</h2>
                      
                      {/* Summary Cards */}
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="bg-white rounded-2xl p-5 border border-gray-100">
                          <p className="text-sm text-gray-500 mb-1">Promo Orders</p>
                          <p className="text-2xl font-black text-gray-900">{promoAnalytics.summary?.total_promo_orders || 0}</p>
                          <p className="text-xs text-gray-400 mt-1">of {promoAnalytics.summary?.total_orders || 0} total</p>
                        </div>
                        <div className="bg-white rounded-2xl p-5 border border-gray-100">
                          <p className="text-sm text-gray-500 mb-1">Conversion Rate</p>
                          <p className="text-2xl font-black text-emerald-600">{promoAnalytics.summary?.conversion_rate || 0}%</p>
                          <p className="text-xs text-gray-400 mt-1">orders with promo</p>
                        </div>
                        <div className="bg-white rounded-2xl p-5 border border-gray-100">
                          <p className="text-sm text-gray-500 mb-1">Total Discount Given</p>
                          <p className="text-2xl font-black text-amber-600">${promoAnalytics.summary?.total_discount_given_aud?.toFixed(2) || '0.00'}</p>
                          <p className="text-xs text-gray-400 mt-1">AUD total</p>
                        </div>
                        <div className="bg-white rounded-2xl p-5 border border-gray-100">
                          <p className="text-sm text-gray-500 mb-1">Revenue w/ Promo</p>
                          <p className="text-2xl font-black text-blue-600">${promoAnalytics.summary?.total_revenue_with_promo_aud?.toFixed(2) || '0.00'}</p>
                          <p className="text-xs text-gray-400 mt-1">{promoAnalytics.summary?.unique_users_with_promo || 0} users</p>
                        </div>
                      </div>

                      {/* Uses Over Time Chart */}
                      {promoAnalytics.timeline && promoAnalytics.timeline.length > 0 && (
                        <div className="bg-white rounded-2xl p-6 border border-gray-100">
                          <h3 className="font-bold text-gray-900 mb-4">Uses Over Time (Last 30 Days)</h3>
                          <div className="flex items-end gap-1 h-32">
                            {promoAnalytics.timeline.map((day, i) => {
                              const maxUses = Math.max(...promoAnalytics.timeline.map(d => d.uses), 1);
                              const height = (day.uses / maxUses) * 100;
                              return (
                                <div key={day.date} className="flex-1 flex flex-col items-center group relative">
                                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap z-10">
                                    {day.date}: {day.uses} use{day.uses !== 1 ? 's' : ''}
                                  </div>
                                  <div
                                    className="w-full bg-emerald-500 rounded-t hover:bg-emerald-400 transition-colors min-h-[2px]"
                                    style={{ height: `${Math.max(height, 2)}%` }}
                                  />
                                </div>
                              );
                            })}
                          </div>
                          <div className="flex justify-between mt-2">
                            <span className="text-xs text-gray-400">{promoAnalytics.timeline[0]?.date}</span>
                            <span className="text-xs text-gray-400">{promoAnalytics.timeline[promoAnalytics.timeline.length - 1]?.date}</span>
                          </div>
                        </div>
                      )}

                      {/* Per-Code Breakdown */}
                      {promoAnalytics.code_stats && promoAnalytics.code_stats.length > 0 && (
                        <div className="bg-white rounded-2xl p-6 border border-gray-100">
                          <h3 className="font-bold text-gray-900 mb-4">Code Performance</h3>
                          <div className="overflow-x-auto">
                            <table className="w-full text-left">
                              <thead>
                                <tr className="border-b border-gray-100">
                                  <th className="pb-3 text-xs font-semibold text-gray-500 uppercase">Code</th>
                                  <th className="pb-3 text-xs font-semibold text-gray-500 uppercase">Uses</th>
                                  <th className="pb-3 text-xs font-semibold text-gray-500 uppercase">Unique Users</th>
                                  <th className="pb-3 text-xs font-semibold text-gray-500 uppercase">Revenue</th>
                                  <th className="pb-3 text-xs font-semibold text-gray-500 uppercase">Discount Given</th>
                                  <th className="pb-3 text-xs font-semibold text-gray-500 uppercase">Top Destinations</th>
                                </tr>
                              </thead>
                              <tbody>
                                {promoAnalytics.code_stats.map(stat => (
                                  <tr key={stat.code} className="border-b border-gray-50">
                                    <td className="py-3"><span className="font-mono font-bold text-sm bg-gray-100 px-2 py-1 rounded">{stat.code}</span></td>
                                    <td className="py-3 font-semibold">{stat.total_uses}</td>
                                    <td className="py-3 text-gray-600">{stat.unique_users}</td>
                                    <td className="py-3 text-emerald-600 font-semibold">${stat.total_revenue?.toFixed(2)}</td>
                                    <td className="py-3 text-amber-600">${stat.total_discount_given?.toFixed(2)}</td>
                                    <td className="py-3 text-xs text-gray-500">
                                      {stat.top_countries?.slice(0, 3).map(([country, count]) => (
                                        <span key={country} className="inline-block mr-1 bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">{country} ({count})</span>
                                      ))}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Top Users */}
                      {promoAnalytics.top_users && promoAnalytics.top_users.length > 0 && (
                        <div className="bg-white rounded-2xl p-6 border border-gray-100">
                          <h3 className="font-bold text-gray-900 mb-4">Top Users by Promo Usage</h3>
                          <div className="space-y-2">
                            {promoAnalytics.top_users.slice(0, 10).map((u, i) => (
                              <div key={u.email} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                                <div className="flex items-center gap-3">
                                  <span className="w-6 h-6 flex items-center justify-center bg-gray-100 rounded-full text-xs font-bold">{i + 1}</span>
                                  <span className="text-sm text-gray-700">{u.email}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="text-sm font-semibold">{u.total_orders_with_promo} orders</span>
                                  <div className="flex gap-1">
                                    {Object.entries(u.codes_used || {}).map(([code, count]) => (
                                      <span key={code} className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{code}({count})</span>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {loadingPromoAnalytics && (
                        <div className="text-center py-8 text-gray-400">Loading analytics...</div>
                      )}
                    </div>
                  )}

                  <div className="flex justify-between items-center">
                    <h2 className="text-xl font-bold text-gray-900">Promo Codes</h2>
                    <button
                      onClick={() => setShowPromoForm(!showPromoForm)}
                      className="flex items-center gap-2 px-5 py-3 bg-black text-white rounded-xl font-bold hover:bg-gray-800 transition-colors"
                    >
                      {showPromoForm ? <X size={18} /> : <Plus size={18} />}
                      {showPromoForm ? 'Cancel' : 'Add Promo'}
                    </button>
                  </div>

                  {/* Add Promo Form - Enhanced */}
                  {showPromoForm && (
                    <form onSubmit={handleAddPromo} className="bg-white rounded-2xl p-6 border border-gray-100 space-y-6">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Code</label>
                          <input
                            type="text"
                            value={newPromoCode}
                            onChange={(e) => setNewPromoCode(e.target.value.toUpperCase())}
                            required
                            placeholder="SUMMER20"
                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-black uppercase"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                          <input
                            type="text"
                            value={newPromoDesc}
                            onChange={(e) => setNewPromoDesc(e.target.value)}
                            placeholder="Summer sale discount"
                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-black"
                          />
                        </div>
                      </div>

                      {/* Discount Type Toggle */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">Discount Type</label>
                        <div className="flex gap-3">
                          <button
                            type="button"
                            onClick={() => setUseSeparateDiscounts(false)}
                            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm transition-colors ${
                              !useSeparateDiscounts
                                ? 'bg-black text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            <Percent size={16} />
                            Single Discount
                          </button>
                          <button
                            type="button"
                            onClick={() => setUseSeparateDiscounts(true)}
                            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm transition-colors ${
                              useSeparateDiscounts
                                ? 'bg-black text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            <Globe size={16} />
                            Local & Regional
                          </button>
                        </div>
                      </div>

                      {/* Discount Input(s) */}
                      {!useSeparateDiscounts ? (
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Discount %</label>
                          <input
                            type="number"
                            value={newPromoDiscount}
                            onChange={(e) => setNewPromoDiscount(e.target.value)}
                            required
                            min="1"
                            max="100"
                            placeholder="20"
                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-black"
                          />
                        </div>
                      ) : (
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div className="bg-blue-50 p-4 rounded-xl">
                            <label className="block text-sm font-semibold text-blue-800 mb-2 flex items-center gap-2">
                              <MapPin size={16} />
                              Local Plans Discount %
                            </label>
                            <input
                              type="number"
                              value={newPromoLocalDiscount}
                              onChange={(e) => setNewPromoLocalDiscount(e.target.value)}
                              min="0"
                              max="100"
                              placeholder="e.g., 15"
                              className="w-full px-4 py-3 rounded-xl border border-blue-200 focus:outline-none focus:border-blue-500 bg-white"
                            />
                            <p className="text-xs text-blue-600 mt-2">Discount for single-country plans</p>
                          </div>
                          <div className="bg-purple-50 p-4 rounded-xl">
                            <label className="block text-sm font-semibold text-purple-800 mb-2 flex items-center gap-2">
                              <Globe size={16} />
                              Regional Plans Discount %
                            </label>
                            <input
                              type="number"
                              value={newPromoRegionalDiscount}
                              onChange={(e) => setNewPromoRegionalDiscount(e.target.value)}
                              min="0"
                              max="100"
                              placeholder="e.g., 10"
                              className="w-full px-4 py-3 rounded-xl border border-purple-200 focus:outline-none focus:border-purple-500 bg-white"
                            />
                            <p className="text-xs text-purple-600 mt-2">Discount for multi-country/regional plans</p>
                          </div>
                        </div>
                      )}

                      {/* Valid For Section */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">Valid For</label>
                        <div className="flex flex-wrap gap-3">
                          {[
                            { value: 'all', label: 'All Plans', icon: Globe },
                            { value: 'local', label: 'Local Plans Only', icon: MapPin },
                            { value: 'regional', label: 'Regional Plans Only', icon: Globe },
                            { value: 'specific', label: 'Specific Countries', icon: MapPin },
                          ].map(option => (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => setPromoValidFor(option.value)}
                              className={`flex items-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm transition-colors ${
                                promoValidFor === option.value
                                  ? 'bg-black text-white'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              <option.icon size={16} />
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Country Selection (if specific) */}
                      {promoValidFor === 'specific' && (
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Select Countries</label>
                          
                          {/* Selected Countries */}
                          {promoCountries.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-3">
                              {promoCountries.map(code => {
                                const country = allCountries.find(c => c.id === code);
                                return (
                                  <span key={code} className="flex items-center gap-2 px-3 py-2 bg-emerald-100 text-emerald-800 rounded-lg text-sm font-semibold">
                                    {country?.name || code}
                                    <button
                                      type="button"
                                      onClick={() => setPromoCountries(promoCountries.filter(c => c !== code))}
                                      className="hover:text-red-600"
                                    >
                                      <X size={14} />
                                    </button>
                                  </span>
                                );
                              })}
                            </div>
                          )}

                          {/* Search Countries */}
                          <div className="relative">
                            <input
                              type="text"
                              value={promoCountrySearch}
                              onChange={(e) => setPromoCountrySearch(e.target.value)}
                              placeholder="Search countries to add..."
                              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-black"
                            />
                            {promoCountrySearch && filteredCountriesForPromo.length > 0 && (
                              <div className="absolute z-10 w-full mt-2 bg-white rounded-xl shadow-lg border border-gray-100 max-h-48 overflow-y-auto">
                                {filteredCountriesForPromo.map(country => (
                                  <button
                                    key={country.id}
                                    type="button"
                                    onClick={() => {
                                      setPromoCountries([...promoCountries, country.id]);
                                      setPromoCountrySearch('');
                                    }}
                                    className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3"
                                  >
                                    {country.flag && (
                                      <img src={country.flag} alt="" className="w-6 h-4 object-cover rounded" />
                                    )}
                                    <span className="font-semibold">{country.name}</span>
                                    <span className="text-gray-400 text-sm">({country.code})</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Usage Limits Section */}
                      <div className="grid sm:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Max Total Uses</label>
                          <input
                            type="number"
                            value={promoMaxUses}
                            onChange={(e) => setPromoMaxUses(e.target.value)}
                            min="1"
                            placeholder="Unlimited"
                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-black"
                          />
                          <p className="text-xs text-gray-500 mt-1">Total uses across all users</p>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Max Per User</label>
                          <input
                            type="number"
                            value={promoMaxUsesPerUser}
                            onChange={(e) => setPromoMaxUsesPerUser(e.target.value)}
                            min="1"
                            placeholder="Unlimited"
                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-black"
                          />
                          <p className="text-xs text-gray-500 mt-1">Uses per customer (e.g., 1)</p>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Expiry Date</label>
                          <input
                            type="date"
                            value={promoExpiryDate}
                            onChange={(e) => setPromoExpiryDate(e.target.value)}
                            min={new Date().toISOString().split('T')[0]}
                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-black"
                          />
                          <p className="text-xs text-gray-500 mt-1">Leave empty for no expiry</p>
                        </div>
                      </div>

                      <button
                        type="submit"
                        disabled={promoLoading || (promoValidFor === 'specific' && promoCountries.length === 0)}
                        className="px-6 py-3 bg-black text-white rounded-xl font-bold hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2"
                      >
                        {promoLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus size={18} />}
                        Add Promo Code
                      </button>
                    </form>
                  )}

                  {/* Promo List */}
                  <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-100">
                          <tr>
                            <th className="text-left px-6 py-4 text-sm font-bold text-gray-500">Code</th>
                            <th className="text-left px-6 py-4 text-sm font-bold text-gray-500">Discount</th>
                            <th className="text-left px-6 py-4 text-sm font-bold text-gray-500">Valid For</th>
                            <th className="text-left px-6 py-4 text-sm font-bold text-gray-500">Description</th>
                            <th className="text-left px-6 py-4 text-sm font-bold text-gray-500">Uses</th>
                            <th className="text-left px-6 py-4 text-sm font-bold text-gray-500">Expiry</th>
                            <th className="text-left px-6 py-4 text-sm font-bold text-gray-500">Status</th>
                            <th className="text-left px-6 py-4 text-sm font-bold text-gray-500">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {promoCodes.length === 0 ? (
                            <tr>
                              <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                                No promo codes yet
                              </td>
                            </tr>
                          ) : (
                            promoCodes.map((promo) => {
                              const isExpired = promo.expiryDate && new Date(promo.expiryDate) < new Date();
                              const isMaxedOut = promo.maxUses && promo.usageCount >= promo.maxUses;
                              return (
                              <tr key={promo.id} className={`hover:bg-gray-50 ${(isExpired || isMaxedOut) ? 'opacity-60' : ''}`}>
                                <td className="px-6 py-4 font-bold text-gray-900">{promo.code}</td>
                                <td className="px-6 py-4 text-gray-900">
                                  {promo.localDiscount !== undefined && promo.localDiscount !== null || 
                                   promo.regionalDiscount !== undefined && promo.regionalDiscount !== null ? (
                                    <div className="space-y-1">
                                      {promo.localDiscount !== undefined && promo.localDiscount !== null && (
                                        <span className="block text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-lg">
                                          Local: {(promo.localDiscount * 100).toFixed(0)}%
                                        </span>
                                      )}
                                      {promo.regionalDiscount !== undefined && promo.regionalDiscount !== null && (
                                        <span className="block text-xs px-2 py-1 bg-purple-100 text-purple-800 rounded-lg">
                                          Regional: {(promo.regionalDiscount * 100).toFixed(0)}%
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <span>{(promo.discount * 100).toFixed(0)}%</span>
                                  )}
                                </td>
                                <td className="px-6 py-4">
                                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                    promo.validFor === 'local' ? 'bg-blue-100 text-blue-800' :
                                    promo.validFor === 'regional' ? 'bg-purple-100 text-purple-800' :
                                    promo.validFor === 'specific' ? 'bg-orange-100 text-orange-800' :
                                    'bg-gray-100 text-gray-700'
                                  }`}>
                                    {getValidForLabel(promo)}
                                  </span>
                                </td>
                                <td className="px-6 py-4 text-gray-600">{promo.description}</td>
                                <td className="px-6 py-4">
                                  <div className="space-y-1">
                                    <div>
                                      <span className="text-gray-900 font-semibold">{promo.usageCount || 0}</span>
                                      {promo.maxUses && (
                                        <span className="text-gray-500">/{promo.maxUses}</span>
                                      )}
                                      {!promo.maxUses && (
                                        <span className="text-gray-400 text-xs ml-1">∞</span>
                                      )}
                                    </div>
                                    {promo.maxUsesPerUser && (
                                      <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">
                                        {promo.maxUsesPerUser}/user
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-6 py-4">
                                  {promo.expiryDate ? (
                                    <span className={`text-sm ${isExpired ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
                                      {new Date(promo.expiryDate).toLocaleDateString()}
                                      {isExpired && <span className="block text-xs">Expired</span>}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400 text-sm">No expiry</span>
                                  )}
                                </td>
                                <td className="px-6 py-4">
                                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                                    isExpired ? 'bg-red-100 text-red-800' :
                                    isMaxedOut ? 'bg-yellow-100 text-yellow-800' :
                                    promo.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                                  }`}>
                                    {isExpired ? 'Expired' : isMaxedOut ? 'Max Used' : promo.isActive ? 'Active' : 'Inactive'}
                                  </span>
                                </td>
                                <td className="px-6 py-4">
                                  <button
                                    onClick={() => deletePromoCode(promo.id)}
                                    className="text-red-600 hover:text-red-800"
                                  >
                                    <Trash2 size={18} />
                                  </button>
                                </td>
                              </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Referrals Tab */}
              {activeTab === 'referrals' && (
                <div className="space-y-6">
                  {/* Referral Stats */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-white rounded-2xl p-5 border border-gray-100">
                      <p className="text-sm text-gray-500 mb-1">Total Referrals</p>
                      <p className="text-3xl font-black text-gray-900">{referralData.stats?.totalReferrals || 0}</p>
                    </div>
                    <div className="bg-white rounded-2xl p-5 border border-gray-100">
                      <p className="text-sm text-gray-500 mb-1">Completed</p>
                      <p className="text-3xl font-black text-emerald-600">{referralData.stats?.completedReferrals || 0}</p>
                    </div>
                    <div className="bg-white rounded-2xl p-5 border border-gray-100">
                      <p className="text-sm text-gray-500 mb-1">Pending</p>
                      <p className="text-3xl font-black text-yellow-600">{referralData.stats?.pendingReferrals || 0}</p>
                    </div>
                    <div className="bg-white rounded-2xl p-5 border border-gray-100">
                      <p className="text-sm text-gray-500 mb-1">Credits Issued</p>
                      <p className="text-3xl font-black text-blue-600">A${(referralData.stats?.totalCreditsIssued || 0).toFixed(2)}</p>
                    </div>
                  </div>

                  {/* Referral Settings */}
                  <div className="bg-white rounded-2xl p-6 border border-gray-100">
                    <h2 className="text-xl font-bold text-gray-900 mb-6">Referral Program Settings</h2>
                    
                    <div className="grid sm:grid-cols-3 gap-6 mb-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Credit Value (AUD)
                        </label>
                        <p className="text-xs text-gray-500 mb-2">Amount referrer receives</p>
                        <input
                          type="number"
                          value={referralSettings.credit_value_aud}
                          onChange={(e) => setReferralSettings({...referralSettings, credit_value_aud: parseFloat(e.target.value)})}
                          min="0"
                          step="0.5"
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-black text-2xl font-bold text-center"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Referred Discount (%)
                        </label>
                        <p className="text-xs text-gray-500 mb-2">Discount for referred users</p>
                        <input
                          type="number"
                          value={(referralSettings.referred_discount * 100).toFixed(0)}
                          onChange={(e) => setReferralSettings({...referralSettings, referred_discount: parseFloat(e.target.value) / 100})}
                          min="0"
                          max="50"
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-black text-2xl font-bold text-center"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Program Status
                        </label>
                        <p className="text-xs text-gray-500 mb-2">Enable/disable referrals</p>
                        <button
                          onClick={() => setReferralSettings({...referralSettings, is_active: !referralSettings.is_active})}
                          className={`w-full px-4 py-3 rounded-xl font-bold text-center ${
                            referralSettings.is_active 
                              ? 'bg-emerald-100 text-emerald-700 border-2 border-emerald-300' 
                              : 'bg-gray-100 text-gray-500 border-2 border-gray-200'
                          }`}
                        >
                          {referralSettings.is_active ? 'ACTIVE' : 'INACTIVE'}
                        </button>
                      </div>
                    </div>

                    <button
                      onClick={handleUpdateReferralSettings}
                      disabled={referralSettingsLoading}
                      className="px-8 py-4 bg-black text-white rounded-xl font-bold hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2"
                    >
                      {referralSettingsLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <Check size={18} /> Save Settings
                        </>
                      )}
                    </button>
                  </div>

                  {/* Referral History */}
                  <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                    <div className="p-4 border-b border-gray-100 bg-gray-50">
                      <h3 className="font-bold text-gray-900">Referral History</h3>
                    </div>
                    
                    {loadingReferrals ? (
                      <div className="flex justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                      </div>
                    ) : referralData.referrals?.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 border-b border-gray-100">
                            <tr>
                              <th className="text-left px-4 py-3 text-sm font-bold text-gray-500">Referrer</th>
                              <th className="text-left px-4 py-3 text-sm font-bold text-gray-500">Referred</th>
                              <th className="text-left px-4 py-3 text-sm font-bold text-gray-500">Status</th>
                              <th className="text-left px-4 py-3 text-sm font-bold text-gray-500">Credit</th>
                              <th className="text-left px-4 py-3 text-sm font-bold text-gray-500">Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {referralData.referrals.map((ref) => (
                              <tr key={ref.referrer_email + ref.referred_email} className="border-b border-gray-50 hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm">{ref.referrer_email}</td>
                                <td className="px-4 py-3 text-sm">{ref.referred_email}</td>
                                <td className="px-4 py-3">
                                  <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                                    ref.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                                    ref.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-gray-100 text-gray-700'
                                  }`}>
                                    {ref.status?.toUpperCase()}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm font-bold">
                                  {ref.status === 'completed' ? `A$${ref.credit_amount?.toFixed(2)}` : '-'}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-500">
                                  {new Date(ref.created_at).toLocaleDateString()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="py-12 text-center text-gray-500">
                        No referrals yet
                      </div>
                    )}
                  </div>

                  {/* Users with Credits */}
                  <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                    <div className="p-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center gap-4 flex-wrap">
                      <h3 className="font-bold text-gray-900">User Credit Balances</h3>
                      <div className="relative flex-1 min-w-[220px] max-w-md">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          value={referralsSearch}
                          onChange={(e) => setReferralsSearch(e.target.value)}
                          placeholder="Search by name, email or referral code…"
                          className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:border-black"
                          data-testid="referrals-search"
                        />
                      </div>
                    </div>
                    
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-100">
                          <tr>
                            <th className="text-left px-4 py-3 text-sm font-bold text-gray-500">User</th>
                            <th className="text-left px-4 py-3 text-sm font-bold text-gray-500">Referral Code</th>
                            <th className="text-left px-4 py-3 text-sm font-bold text-gray-500">Balance</th>
                            <th className="text-left px-4 py-3 text-sm font-bold text-gray-500">Referrals</th>
                            <th className="text-left px-4 py-3 text-sm font-bold text-gray-500">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {customers.filter(c => {
                            if (!(c.credit_balance_aud > 0 || c.referral_code)) return false;
                            if (!referralsSearch) return true;
                            const q = referralsSearch.toLowerCase();
                            const email = (c.email || '').toLowerCase();
                            const name = (c.name || `${c.first_name || c.firstName || ''} ${c.last_name || c.lastName || ''}`).toLowerCase();
                            const code = (c.referral_code || '').toLowerCase();
                            return email.includes(q) || name.includes(q) || code.includes(q);
                          }).map((cust) => (
                            <tr key={cust.email} className="border-b border-gray-50 hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm">{cust.email}</td>
                              <td className="px-4 py-3 text-sm font-mono">{cust.referral_code || '-'}</td>
                              <td className="px-4 py-3 text-sm font-bold text-emerald-600">
                                A${(cust.credit_balance_aud || 0).toFixed(2)}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {cust.successful_referrals || 0} / {cust.total_referrals || 0}
                              </td>
                              <td className="px-4 py-3">
                                <button
                                  onClick={() => {
                                    setSelectedUserForCredits(cust);
                                    setShowAddCreditsModal(true);
                                  }}
                                  className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm font-bold hover:bg-blue-200"
                                >
                                  + Add Credits
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Marketing Tab */}
              {activeTab === 'marketing' && (
                <div className="space-y-6">
                  {/* Email Composer */}
                  <div className="bg-white rounded-2xl p-6 border border-gray-100">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                          <Mail size={24} /> Marketing Email Campaign
                        </h2>
                        <p className="text-gray-500 text-sm mt-1">Send promotional emails to your customers</p>
                      </div>
                      {emailSentSuccess && (
                        <div className="flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-xl">
                          <CheckCircle size={18} />
                          <span className="font-semibold">Emails sent successfully!</span>
                        </div>
                      )}
                    </div>

                    {/* Recipients Selection */}
                    <div className="mb-6">
                      <label className="block text-sm font-bold text-gray-700 mb-3">Recipients</label>
                      <div className="flex gap-3 mb-4">
                        <button
                          onClick={() => setMarketingRecipients('all')}
                          className={`flex-1 py-3 px-4 rounded-xl font-bold transition-all ${
                            marketingRecipients === 'all'
                              ? 'bg-black text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          <Users size={18} className="inline mr-2" />
                          All Customers ({customers.length})
                        </button>
                        <button
                          onClick={() => setMarketingRecipients('selected')}
                          className={`flex-1 py-3 px-4 rounded-xl font-bold transition-all ${
                            marketingRecipients === 'selected'
                              ? 'bg-black text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          <Check size={18} className="inline mr-2" />
                          Select Specific ({selectedUsers.length})
                        </button>
                      </div>

                      {/* User Selection Panel */}
                      {marketingRecipients === 'selected' && (
                        <div className="border border-gray-200 rounded-xl overflow-hidden">
                          <div className="bg-gray-50 p-3 border-b border-gray-200">
                            <div className="flex items-center gap-3">
                              <input
                                type="text"
                                placeholder="Search users by name or email..."
                                value={userSearchTerm}
                                onChange={(e) => setUserSearchTerm(e.target.value)}
                                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                              />
                              <button
                                onClick={selectAllFilteredUsers}
                                className="px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg font-semibold hover:bg-emerald-200"
                              >
                                {filteredUsersForMarketing.every(u => selectedUsers.includes(u.email)) ? 'Deselect All' : 'Select All'}
                              </button>
                            </div>
                          </div>
                          <div className="max-h-64 overflow-y-auto">
                            {filteredUsersForMarketing.length === 0 ? (
                              <div className="p-4 text-center text-gray-500">No users found</div>
                            ) : (
                              filteredUsersForMarketing.map(customer => (
                                <div
                                  key={customer.email}
                                  onClick={() => toggleUserSelection(customer.email)}
                                  className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-0 ${
                                    selectedUsers.includes(customer.email) ? 'bg-emerald-50' : ''
                                  }`}
                                >
                                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                    selectedUsers.includes(customer.email)
                                      ? 'bg-emerald-500 border-emerald-500'
                                      : 'border-gray-300'
                                  }`}>
                                    {selectedUsers.includes(customer.email) && <Check size={14} className="text-white" />}
                                  </div>
                                  <div className="flex-1">
                                    <p className="font-semibold text-gray-900">{customer.name || 'No name'}</p>
                                    <p className="text-sm text-gray-500">{customer.email}</p>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                          {selectedUsers.length > 0 && (
                            <div className="bg-emerald-50 px-4 py-2 border-t border-emerald-100">
                              <span className="text-sm font-semibold text-emerald-700">
                                {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Email Subject */}
                    <div className="mb-4">
                      <label className="block text-sm font-bold text-gray-700 mb-2">Subject Line</label>
                      <input
                        type="text"
                        value={emailSubject}
                        onChange={(e) => setEmailSubject(e.target.value)}
                        placeholder="e.g., Exclusive 30% Off All eSIMs This Weekend!"
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 font-medium"
                      />
                    </div>

                    {/* Email Content Editor */}
                    <div className="mb-6">
                      <label className="block text-sm font-bold text-gray-700 mb-2">Email Content</label>
                      <div className="border border-gray-200 rounded-xl overflow-hidden">
                        {/* Simple Toolbar */}
                        <div className="bg-gray-50 px-3 py-2 border-b border-gray-200 flex items-center gap-1">
                          <span className="text-xs text-gray-500 mr-2">Formatting tips: Use line breaks for paragraphs</span>
                        </div>
                        {/* Content Area */}
                        <textarea
                          value={emailContent}
                          onChange={(e) => setEmailContent(e.target.value)}
                          placeholder="Write your marketing message here...

Example:
Hey there, traveller! 

We've got something special just for you - 30% OFF all eSIM plans this weekend only!

Whether you're planning a trip to Japan, exploring Europe, or adventuring through Southeast Asia, now's the perfect time to grab your eSIM.

Use code: WEEKEND30 at checkout.

Happy travels!
The Connectify Team"
                          className="w-full px-4 py-4 min-h-[300px] focus:outline-none resize-none font-medium text-gray-800"
                          style={{ lineHeight: '1.8' }}
                        />
                      </div>
                    </div>

                    {/* Email Images */}
                    <div className="mb-6">
                      <label className="block text-sm font-bold text-gray-700 mb-2">Images (optional)</label>
                      <p className="text-xs text-gray-400 mb-3">Upload images or paste URLs. Images appear above the text content.</p>
                      {emailImages.map((url, i) => (
                        <div key={i} className="flex items-center gap-2 mb-2">
                          <input
                            type="text"
                            value={url}
                            onChange={(e) => {
                              const updated = [...emailImages];
                              updated[i] = e.target.value;
                              setEmailImages(updated);
                            }}
                            placeholder="https://example.com/image.jpg"
                            className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                          />
                          {url.trim() && (
                            <img src={url} alt="" className="w-10 h-10 object-cover rounded-lg border border-gray-200" onError={(e) => e.target.style.display = 'none'} />
                          )}
                          <button
                            onClick={() => setEmailImages(emailImages.filter((_, idx) => idx !== i))}
                            className="p-2 text-gray-400 hover:text-red-500"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setEmailImages([...emailImages, ''])}
                          className="flex items-center gap-2 px-4 py-2 bg-gray-50 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-100"
                        >
                          <Plus size={16} /> Add Image URL
                        </button>
                        <label className={`flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 cursor-pointer ${uploadingImage ? 'opacity-50 pointer-events-none' : ''}`}>
                          {uploadingImage ? (
                            <><Loader2 size={16} className="animate-spin" /> Uploading...</>
                          ) : (
                            <><Upload size={16} /> Upload Image</>
                          )}
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/gif,image/webp"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              setUploadingImage(true);
                              try {
                                const formData = new FormData();
                                formData.append('file', file);
                                const resp = await axios.post(`${API}/admin/upload-image`, formData, {
                                  headers: { 'Content-Type': 'multipart/form-data' }
                                });
                                if (resp.data.success) {
                                  setEmailImages(prev => [...prev, resp.data.url]);
                                }
                              } catch (err) {
                                setEmailError('Failed to upload image: ' + (err.response?.data?.detail || err.message));
                              } finally {
                                setUploadingImage(false);
                                e.target.value = '';
                              }
                            }}
                          />
                        </label>
                      </div>
                    </div>

                    {/* Error Message */}
                    {emailError && (
                      <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-red-700">
                        <AlertCircle size={18} />
                        <span>{emailError}</span>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex items-center gap-3">
                      <button
                        onClick={generateEmailPreview}
                        disabled={!emailContent.trim()}
                        className="flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Eye size={18} />
                        Preview Email
                      </button>
                      <button
                        onClick={sendMarketingEmail}
                        disabled={sendingEmail || !emailSubject.trim() || !emailContent.trim()}
                        className="flex items-center gap-2 px-8 py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {sendingEmail ? (
                          <>
                            <Loader2 size={18} className="animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send size={18} />
                            Send to {marketingRecipients === 'all' ? `${customers.length} customers` : `${selectedUsers.length} selected`}
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Email Preview Modal */}
                  {showEmailPreview && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
                        <div className="flex items-center justify-between p-4 border-b border-gray-200">
                          <h3 className="text-lg font-bold">Email Preview</h3>
                          <button
                            onClick={() => setShowEmailPreview(false)}
                            className="p-2 hover:bg-gray-100 rounded-xl"
                          >
                            <X size={20} />
                          </button>
                        </div>
                        <div className="p-4 bg-gray-100 overflow-y-auto max-h-[70vh]">
                          <div className="mb-3 px-2">
                            <p className="text-sm text-gray-600"><strong>Subject:</strong> {emailSubject || '(No subject)'}</p>
                          </div>
                          <div 
                            className="bg-white rounded-lg shadow-sm"
                            dangerouslySetInnerHTML={{ __html: emailPreviewHtml }}
                          />
                        </div>
                        <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
                          <button
                            onClick={() => setShowEmailPreview(false)}
                            className="px-6 py-2 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200"
                          >
                            Close
                          </button>
                          <button
                            onClick={() => {
                              setShowEmailPreview(false);
                              sendMarketingEmail();
                            }}
                            disabled={sendingEmail}
                            className="px-6 py-2 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700"
                          >
                            <Send size={16} className="inline mr-2" />
                            Send Email
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Featured Destinations Tab */}
              {activeTab === 'featured' && (
                <div className="space-y-6" data-testid="featured-destinations-tab">
                  <div className="bg-white rounded-2xl p-6 border border-gray-100">
                    <h2 className="text-xl font-bold text-gray-900 mb-2">Featured Destinations</h2>
                    <p className="text-gray-600 mb-6">
                      Select 4-8 destinations to display in the "Popular Destinations" section on the home page.
                    </p>

                    {/* Selected destinations */}
                    <div className="mb-6">
                      <p className="text-sm font-semibold text-gray-700 mb-3">
                        Selected ({featuredDestinations.length}/8):
                        {featuredDestinations.length < 4 && <span className="text-red-500 ml-2">Minimum 4 required</span>}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {featuredDestinations.map(code => {
                          const dest = allCountries.find(c => c.id === code || c.code === code);
                          return (
                            <div key={code} className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5">
                              {dest?.flag && !dest?.isRegion ? (
                                <img src={dest.flag} alt="" className="w-5 h-3.5 rounded object-cover" />
                              ) : (
                                <Globe size={14} className="text-emerald-600" />
                              )}
                              <span className="text-sm font-medium text-gray-800">{dest?.name || code}</span>
                              <button
                                onClick={() => setFeaturedDestinations(prev => prev.filter(c => c !== code))}
                                className="text-gray-400 hover:text-red-500 ml-1"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Search to add */}
                    <div className="relative mb-4">
                      <input
                        type="text"
                        placeholder="Search countries or regions to add..."
                        value={featuredSearch}
                        onChange={(e) => setFeaturedSearch(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-emerald-500 text-sm"
                        data-testid="featured-search-input"
                      />
                    </div>

                    {/* Available destinations */}
                    <div className="max-h-80 overflow-y-auto border border-gray-100 rounded-xl">
                      {allCountries
                        .filter(c => {
                          if (!featuredSearch.trim()) return true;
                          return c.name.toLowerCase().includes(featuredSearch.toLowerCase());
                        })
                        .filter(c => !featuredDestinations.includes(c.id) && !featuredDestinations.includes(c.code))
                        .slice(0, 30)
                        .map(country => (
                          <button
                            key={country.id || country.code}
                            onClick={() => {
                              if (featuredDestinations.length >= 8) return;
                              const code = country.code || country.id;
                              setFeaturedDestinations(prev => [...prev, code]);
                            }}
                            disabled={featuredDestinations.length >= 8}
                            className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-emerald-50 border-b border-gray-50 last:border-0 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            {country.isRegion ? (
                              <div className="w-7 h-5 bg-gradient-to-br from-blue-500 to-emerald-500 rounded flex items-center justify-center">
                                <Globe size={12} className="text-white" />
                              </div>
                            ) : (
                              <img src={country.flag} alt="" className="w-7 h-5 rounded object-cover" />
                            )}
                            <span className="text-sm font-medium text-gray-800">{country.name}</span>
                            {country.isRegion && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Region</span>}
                            <Plus size={16} className="ml-auto text-emerald-500" />
                          </button>
                        ))
                      }
                    </div>

                    {/* Save button */}
                    <div className="mt-6 flex justify-end">
                      <button
                        onClick={async () => {
                          setSavingFeatured(true);
                          const result = await saveFeaturedDestinations(featuredDestinations);
                          setSavingFeatured(false);
                          if (result && result.success) {
                            alert('Featured destinations saved!');
                          } else {
                            alert(result?.message || 'Please select between 4 and 8 destinations');
                          }
                        }}
                        disabled={featuredDestinations.length < 4 || featuredDestinations.length > 8 || savingFeatured}
                        className="px-6 py-3 bg-black text-white rounded-xl font-bold hover:bg-gray-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="save-featured-btn"
                      >
                        {savingFeatured ? 'Saving...' : 'Save Featured Destinations'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Pricing Tab */}
              {activeTab === 'pricing' && (
                <div className="space-y-6">
                  <div className="bg-white rounded-2xl p-6 border border-gray-100">
                    <h2 className="text-xl font-bold text-gray-900 mb-2">Markup Settings Grid</h2>
                    <p className="text-gray-600 mb-6">
                      Set markup percentages for each combination of plan type and base API price.
                    </p>

                    {/* Quick Set Row Buttons */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="text-sm text-gray-500 mr-2">Quick set row:</span>
                      <button onClick={() => setRowValues('local', 40)} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs font-bold hover:bg-blue-200">
                        Local → 40%
                      </button>
                      <button onClick={() => setRowValues('regional', 25)} className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-lg text-xs font-bold hover:bg-emerald-200">
                        Regional → 25%
                      </button>
                      <button onClick={() => setRowValues('global', 20)} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-lg text-xs font-bold hover:bg-purple-200">
                        Global → 20%
                      </button>
                    </div>

                    {/* Markup Grid Table */}
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="px-3 py-3 text-left text-sm font-bold text-gray-700 border border-gray-200 min-w-[100px]">
                              Plan Type
                            </th>
                            <th className="px-3 py-3 text-center text-sm font-bold text-gray-700 border border-gray-200 min-w-[80px]">
                              $0-$3
                            </th>
                            <th className="px-3 py-3 text-center text-sm font-bold text-gray-700 border border-gray-200 min-w-[80px]">
                              $3-$6
                            </th>
                            <th className="px-3 py-3 text-center text-sm font-bold text-gray-700 border border-gray-200 min-w-[80px]">
                              $6-$10
                            </th>
                            <th className="px-3 py-3 text-center text-sm font-bold text-gray-700 border border-gray-200 min-w-[80px]">
                              $10-$15
                            </th>
                            <th className="px-3 py-3 text-center text-sm font-bold text-gray-700 border border-gray-200 min-w-[80px]">
                              $15-$20
                            </th>
                            <th className="px-3 py-3 text-center text-sm font-bold text-gray-700 border border-gray-200 min-w-[80px]">
                              $20+
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {/* Local Row */}
                          <tr className="bg-blue-50/50">
                            <td className="px-3 py-2 border border-gray-200">
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                                <span className="font-semibold text-gray-900">Local</span>
                              </div>
                              <span className="text-xs text-gray-500">Single country</span>
                            </td>
                            {['0_3', '3_6', '6_10', '10_15', '15_20', '20_plus'].map(tier => (
                              <td key={`local_${tier}`} className="px-2 py-2 border border-gray-200">
                                <div className="flex items-center justify-center">
                                  <input
                                    type="number"
                                    value={markupGrid[`local_${tier}`]}
                                    onChange={(e) => updateMarkupCell(`local_${tier}`, e.target.value)}
                                    min="0"
                                    max="200"
                                    className="w-16 px-2 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-blue-500 text-center font-bold"
                                  />
                                  <span className="ml-1 text-gray-500">%</span>
                                </div>
                              </td>
                            ))}
                          </tr>
                          {/* Regional Row */}
                          <tr className="bg-emerald-50/50">
                            <td className="px-3 py-2 border border-gray-200">
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                                <span className="font-semibold text-gray-900">Regional</span>
                              </div>
                              <span className="text-xs text-gray-500">Multi-country</span>
                            </td>
                            {['0_3', '3_6', '6_10', '10_15', '15_20', '20_plus'].map(tier => (
                              <td key={`regional_${tier}`} className="px-2 py-2 border border-gray-200">
                                <div className="flex items-center justify-center">
                                  <input
                                    type="number"
                                    value={markupGrid[`regional_${tier}`]}
                                    onChange={(e) => updateMarkupCell(`regional_${tier}`, e.target.value)}
                                    min="0"
                                    max="200"
                                    className="w-16 px-2 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-emerald-500 text-center font-bold"
                                  />
                                  <span className="ml-1 text-gray-500">%</span>
                                </div>
                              </td>
                            ))}
                          </tr>
                          {/* Global Row */}
                          <tr className="bg-purple-50/50">
                            <td className="px-3 py-2 border border-gray-200">
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                                <span className="font-semibold text-gray-900">Global</span>
                              </div>
                              <span className="text-xs text-gray-500">Worldwide</span>
                            </td>
                            {['0_3', '3_6', '6_10', '10_15', '15_20', '20_plus'].map(tier => (
                              <td key={`global_${tier}`} className="px-2 py-2 border border-gray-200">
                                <div className="flex items-center justify-center">
                                  <input
                                    type="number"
                                    value={markupGrid[`global_${tier}`]}
                                    onChange={(e) => updateMarkupCell(`global_${tier}`, e.target.value)}
                                    min="0"
                                    max="200"
                                    className="w-16 px-2 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-purple-500 text-center font-bold"
                                  />
                                  <span className="ml-1 text-gray-500">%</span>
                                </div>
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Example Calculation */}
                    <div className="mt-6 p-4 bg-gray-50 rounded-xl border border-gray-100">
                      <p className="text-sm text-gray-700">
                        <strong>Example:</strong> A local plan with base price $5 (falls in $3-$6 tier) with {markupGrid.local_3_6}% markup = 
                        <span className="font-bold text-blue-600"> ${(5 * (1 + markupGrid.local_3_6 / 100)).toFixed(2)}</span>
                      </p>
                    </div>

                    {/* Save Button */}
                    <button
                      onClick={handleUpdateMarkup}
                      disabled={markupLoading}
                      className="mt-6 px-8 py-4 bg-black text-white rounded-xl font-bold hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2"
                    >
                      {markupLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <Check size={18} /> Save Markup Settings
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Blocked Destinations Tab */}
              {activeTab === 'blocked' && (
                <div className="bg-white rounded-3xl border border-gray-200 overflow-hidden">
                  <div className="p-6 border-b border-gray-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">Blocked Destinations</h2>
                        <p className="text-gray-500 text-sm mt-1">Manage which destinations are hidden from customers</p>
                      </div>
                      <div className="flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-full">
                        <Shield size={16} className="text-gray-600" />
                        <span className="font-bold text-gray-900">{blockedDestinations.length}</span>
                        <span className="text-gray-500 text-sm">blocked</span>
                      </div>
                    </div>
                  </div>

                  <div className="p-6">
                    {/* Add new blocked destination */}
                    <div className="bg-gray-50 rounded-2xl p-4 mb-6">
                      <h3 className="font-bold text-gray-900 mb-3">Block a Destination</h3>
                      <div className="flex gap-3">
                        <input
                          type="text"
                          placeholder="Country Code (e.g., AE)"
                          value={newBlockedCode}
                          onChange={(e) => setNewBlockedCode(e.target.value.toUpperCase())}
                          className="flex-1 px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-black/5"
                          maxLength={5}
                        />
                        <input
                          type="text"
                          placeholder="Country Name (optional)"
                          value={newBlockedName}
                          onChange={(e) => setNewBlockedName(e.target.value)}
                          className="flex-1 px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-black/5"
                        />
                        <button
                          onClick={handleAddBlockedDestination}
                          disabled={!newBlockedCode.trim()}
                          className="px-6 py-3 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          <Plus size={18} />
                          Block
                        </button>
                      </div>
                    </div>

                    {/* List of blocked destinations */}
                    {loadingBlocked ? (
                      <div className="flex justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                      </div>
                    ) : blockedDestinations.length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        <Globe size={48} className="mx-auto mb-4 text-gray-300" />
                        <p className="font-medium">No blocked destinations</p>
                        <p className="text-sm">All destinations are currently visible to customers</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {blockedDestinations.map((code) => (
                          <div key={code} className="flex items-center justify-between bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                                <Shield size={18} className="text-red-600" />
                              </div>
                              <div>
                                <p className="font-bold text-gray-900">{code}</p>
                                <p className="text-sm text-gray-500">{getCountryNameFromCode(code)}</p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleRemoveBlockedDestination(code)}
                              className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                              title="Unblock destination"
                            >
                              <X size={18} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Packages Cache Tab */}
              {activeTab === 'cache' && (
                <div className="bg-white rounded-3xl border border-gray-200 overflow-hidden" data-testid="cache-panel">
                  <div className="p-6 border-b border-gray-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">Packages Cache</h2>
                        <p className="text-gray-500 text-sm mt-1">
                          Automatically refreshes every day at 2:00 AM AWST. Cache TTL: {cacheStatus?.ttl_hours || 24}h.
                        </p>
                      </div>
                      <button
                        data-testid="refresh-packages-cache-btn"
                        onClick={handleRefreshPackagesCache}
                        disabled={cacheRefreshing}
                        className="px-5 py-3 bg-black text-white rounded-xl font-bold hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {cacheRefreshing ? <Loader2 size={18} className="animate-spin" /> : <Package size={18} />}
                        {cacheRefreshing ? 'Refreshing…' : 'Refresh All Packages Now'}
                      </button>
                    </div>
                  </div>

                  <div className="p-6 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="bg-gray-50 rounded-2xl p-4">
                        <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">State</p>
                        <p className="text-xl font-bold text-gray-900 mt-1 capitalize" data-testid="cache-state">
                          {cacheStatus?.status?.state || '—'}
                        </p>
                      </div>
                      <div className="bg-gray-50 rounded-2xl p-4">
                        <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Cached destinations</p>
                        <p className="text-xl font-bold text-gray-900 mt-1" data-testid="cache-count">
                          {cacheStatus?.cached_count ?? 0}
                        </p>
                      </div>
                      <div className="bg-gray-50 rounded-2xl p-4">
                        <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Latest entry</p>
                        <p className="text-sm font-semibold text-gray-900 mt-1">
                          {cacheStatus?.latest_cached_at ? new Date(cacheStatus.latest_cached_at).toLocaleString() : '—'}
                        </p>
                      </div>
                      <div className="bg-gray-50 rounded-2xl p-4">
                        <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Next scheduled run</p>
                        <p className="text-sm font-semibold text-gray-900 mt-1">
                          {cacheStatus?.status?.next_scheduled_at ? new Date(cacheStatus.status.next_scheduled_at).toLocaleString() : '—'}
                        </p>
                      </div>
                    </div>

                    {cacheStatus?.status?.state === 'running' && (
                      <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4">
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-semibold text-blue-900 flex items-center gap-2">
                            <Loader2 size={16} className="animate-spin" />
                            Refresh in progress
                            {cacheStatus.status.trigger ? <span className="text-xs font-normal text-blue-700">({cacheStatus.status.trigger})</span> : null}
                          </p>
                          <p className="text-sm font-bold text-blue-900" data-testid="cache-progress">
                            {cacheStatus.status.progress?.current || 0} / {cacheStatus.status.progress?.total || 0}
                          </p>
                        </div>
                        <div className="w-full bg-blue-100 rounded-full h-2 overflow-hidden">
                          <div
                            className="bg-blue-600 h-2 transition-all"
                            style={{
                              width: cacheStatus.status.progress?.total
                                ? `${(cacheStatus.status.progress.current / cacheStatus.status.progress.total) * 100}%`
                                : '0%'
                            }}
                          />
                        </div>
                        {cacheStatus.status.progress?.current_id && (
                          <p className="text-xs text-blue-700 mt-2">
                            Currently refreshing: <span className="font-mono font-semibold">{cacheStatus.status.progress.current_id}</span>
                          </p>
                        )}
                      </div>
                    )}

                    {cacheStatus?.status?.last_completed_at && cacheStatus.status.state !== 'running' && (
                      <div className="bg-green-50 border border-green-100 rounded-2xl p-4">
                        <p className="text-sm text-green-900">
                          <Check size={16} className="inline mr-1" />
                          Last completed: <span className="font-semibold">{new Date(cacheStatus.status.last_completed_at).toLocaleString()}</span>
                          {cacheStatus.status.trigger ? ` (${cacheStatus.status.trigger})` : ''}
                        </p>
                      </div>
                    )}

                    {cacheStatus?.status?.last_error && (
                      <div className="bg-red-50 border border-red-100 rounded-2xl p-4">
                        <p className="text-sm text-red-900">
                          <AlertCircle size={16} className="inline mr-1" />
                          Last error: <span className="font-mono">{cacheStatus.status.last_error}</span>
                        </p>
                      </div>
                    )}

                    <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4" data-testid="roamify-diagnostic-panel">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div>
                          <p className="font-bold text-gray-900">Roamify connectivity diagnostic</p>
                          <p className="text-xs text-gray-500 mt-1">
                            Shows THIS server&apos;s outbound IP and how Roamify responds to the public vs
                            restricted (order/apply) endpoints. Run this on the live site to get the exact IP to allow-list.
                          </p>
                        </div>
                        <button
                          data-testid="run-roamify-diagnostic-btn"
                          onClick={runRoamifyDiagnostic}
                          disabled={roamifyDiagLoading}
                          className="px-5 py-3 bg-black text-white rounded-xl font-bold hover:bg-gray-800 disabled:bg-gray-400 flex items-center gap-2"
                        >
                          {roamifyDiagLoading ? <Loader2 size={18} className="animate-spin" /> : <AlertCircle size={18} />}
                          {roamifyDiagLoading ? 'Checking…' : 'Run diagnostic'}
                        </button>
                      </div>
                      {roamifyDiag && !roamifyDiag.error && (
                        <div className="mt-4 space-y-2 text-sm">
                          <div className="flex flex-wrap gap-x-6 gap-y-1">
                            <span className="text-gray-700">Egress IP: <span className="font-mono font-bold" data-testid="roamify-egress-ip">{(roamifyDiag.egress_ips_seen && roamifyDiag.egress_ips_seen.length ? roamifyDiag.egress_ips_seen.join(', ') : roamifyDiag.egress_ip)}</span></span>
                            <span className="text-gray-700">Token: <span className="font-mono">{roamifyDiag.token_fingerprint}</span></span>
                            <span className="text-gray-700">Proxy: <span className="font-mono">{roamifyDiag.proxy_configured ? 'yes' : 'no'}</span></span>
                          </div>
                          <div className="flex flex-wrap gap-x-6 gap-y-1">
                            <span className="text-gray-700">/countries: <span className={`font-mono font-bold ${roamifyDiag.checks?.countries_public?.status === 200 ? 'text-green-600' : 'text-red-600'}`}>{String(roamifyDiag.checks?.countries_public?.status)}</span></span>
                            <span className="text-gray-700">/packages (restricted): <span className={`font-mono font-bold ${roamifyDiag.checks?.packages_restricted?.status === 200 ? 'text-green-600' : 'text-red-600'}`} data-testid="roamify-packages-status">{String(roamifyDiag.checks?.packages_restricted?.status)}</span></span>
                          </div>
                          <div className={`rounded-xl p-3 mt-1 ${roamifyDiag.checks?.packages_restricted?.status === 200 ? 'bg-green-50 text-green-900 border border-green-100' : 'bg-amber-50 text-amber-900 border border-amber-100'}`} data-testid="roamify-verdict">
                            {roamifyDiag.verdict}
                          </div>
                          {roamifyDiag.checks?.packages_restricted?.status !== 200 && (
                            <p className="text-xs text-gray-500 font-mono break-all">raw: {roamifyDiag.checks?.packages_restricted?.body}</p>
                          )}
                        </div>
                      )}
                      {roamifyDiag?.error && (
                        <p className="mt-3 text-sm text-red-600" data-testid="roamify-diagnostic-error">{roamifyDiag.error}</p>
                      )}
                    </div>

                    <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4" data-testid="feed-health-panel">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div>
                          <p className="font-bold text-gray-900">Google Shopping feed health</p>
                          <p className="text-xs text-gray-500 mt-1">
                            Validates the live product feed against Merchant Center limits — ID length,
                            uniqueness &amp; format, title/description length, required fields and price —
                            so you catch a rejectable feed before Google fetches it.
                          </p>
                        </div>
                        <button
                          data-testid="run-feed-health-btn"
                          onClick={runFeedHealth}
                          disabled={feedHealthLoading}
                          className="px-5 py-3 bg-black text-white rounded-xl font-bold hover:bg-gray-800 disabled:bg-gray-400 flex items-center gap-2"
                        >
                          {feedHealthLoading ? <Loader2 size={18} className="animate-spin" /> : <ShoppingCart size={18} />}
                          {feedHealthLoading ? 'Checking…' : 'Check feed'}
                        </button>
                      </div>
                      {feedHealth && !feedHealth.error && (
                        <div className="mt-4 space-y-3 text-sm">
                          <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
                            <span className="text-gray-700">Items: <span className="font-mono font-bold" data-testid="feed-health-total">{feedHealth.total_items?.toLocaleString?.() ?? feedHealth.total_items}</span></span>
                            <span className="text-gray-700">Longest ID: <span className="font-mono">{feedHealth.max_id_length}</span></span>
                            <span className="text-gray-700">Longest title: <span className="font-mono">{feedHealth.max_title_length}</span></span>
                            <span
                              data-testid="feed-health-verdict"
                              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-bold ${feedHealth.all_pass ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
                            >
                              {feedHealth.all_pass ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
                              {feedHealth.all_pass ? 'All checks passed' : 'Issues found'}
                            </span>
                          </div>
                          <div className="divide-y divide-gray-200 border border-gray-200 rounded-xl overflow-hidden bg-white">
                            {feedHealth.checks?.map((c) => (
                              <div key={c.label} className="flex items-start justify-between gap-3 px-4 py-2.5" data-testid={`feed-check-${c.label}`}>
                                <div className="flex items-center gap-2 min-w-0">
                                  {c.pass
                                    ? <CheckCircle size={16} className="text-green-600 shrink-0" />
                                    : <X size={16} className="text-red-600 shrink-0" />}
                                  <div className="min-w-0">
                                    <p className="font-semibold text-gray-900">{c.label}</p>
                                    <p className="text-xs text-gray-500">{c.note}</p>
                                    {!c.pass && c.sample?.length > 0 && (
                                      <p className="text-xs text-red-500 font-mono break-all mt-0.5">e.g. {c.sample.join(', ')}</p>
                                    )}
                                  </div>
                                </div>
                                <span className={`font-mono text-xs font-bold shrink-0 ${c.pass ? 'text-green-600' : 'text-red-600'}`}>
                                  {c.pass ? 'OK' : `${c.failures} fail`}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {feedHealth?.error && (
                        <p className="mt-3 text-sm text-red-600" data-testid="feed-health-error">{feedHealth.error}</p>
                      )}
                    </div>

                    <p className="text-xs text-gray-400">
                      Note: refreshes use a {cacheStatus?.delay_seconds || 10}-second delay between requests to stay within Roamify rate limits.
                    </p>
                  </div>
                </div>
              )}

              {/* Manual eSIM Tab */}
              {activeTab === 'manual-esim' && (
                <ManualEsim customers={customers} />
              )}

              {/* Blog Tab */}
              {activeTab === 'blog' && (
                <BlogManager />
              )}

              {/* Destination Name Overrides Tab */}
              {activeTab === 'names' && (
                <CountryNamesPanel />
              )}

              {/* Destination Grouping Tab */}
              {activeTab === 'grouping' && (
                <DestinationGroupingPanel />
              )}

              {/* Assistant Knowledge Base Tab */}
              {activeTab === 'assistant-kb' && (
                <AssistantKbPanel />
              )}

              {/* Welcome Popup Tab */}
              {activeTab === 'welcome-popup' && (
                <div className="bg-white rounded-3xl border border-gray-200 overflow-hidden" data-testid="welcome-popup-panel">
                  <div className="p-6 border-b border-gray-100">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div>
                        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                          <Sparkles size={20} className="text-emerald-600" />
                          Welcome Popup
                        </h2>
                        <p className="text-gray-500 text-sm mt-1">
                          Shown once per browser session on the first page load. Edit content below — visitors who already dismissed it this session won&apos;t see updates until they reopen the site.
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <label className="inline-flex items-center gap-3 cursor-pointer select-none">
                          <span className="text-sm font-semibold text-gray-700">
                            {popupConfig.enabled ? 'Active' : 'Inactive'}
                          </span>
                          <span className="relative inline-block w-12 h-6">
                            <input
                              type="checkbox"
                              checked={popupConfig.enabled}
                              onChange={(e) => setPopupConfig((p) => ({ ...p, enabled: e.target.checked }))}
                              className="sr-only peer"
                              data-testid="welcome-popup-enabled-toggle"
                            />
                            <span className="absolute inset-0 rounded-full bg-gray-300 peer-checked:bg-emerald-500 transition-colors"></span>
                            <span className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform peer-checked:translate-x-6"></span>
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {popupLoading ? (
                    <div className="flex justify-center py-16">
                      <Loader2 className="w-7 h-7 animate-spin text-gray-400" />
                    </div>
                  ) : (
                    <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {/* Editor */}
                      <div className="space-y-5">
                        <div>
                          <label className="block text-sm font-semibold text-gray-800 mb-2">Title</label>
                          <input
                            type="text"
                            value={popupConfig.title}
                            onChange={(e) => setPopupConfig((p) => ({ ...p, title: e.target.value }))}
                            placeholder="e.g. Welcome to Connectify eSIM"
                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none"
                            data-testid="welcome-popup-title-input"
                            maxLength={120}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-800 mb-2">
                            Body (Markdown supported)
                          </label>
                          <textarea
                            value={popupConfig.body}
                            onChange={(e) => setPopupConfig((p) => ({ ...p, body: e.target.value }))}
                            placeholder="Use **bold**, *italic*, [links](https://...) and lists.&#10;&#10;Stay connected anywhere in the world."
                            rows={7}
                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none font-mono text-sm"
                            data-testid="welcome-popup-body-input"
                            maxLength={2000}
                          />
                          <p className="text-xs text-gray-400 mt-1">{popupConfig.body.length}/2000 characters</p>
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-800 mb-2">Image</label>
                          <div className="flex items-center gap-3 flex-wrap">
                            <label
                              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 hover:bg-gray-100 cursor-pointer font-semibold text-gray-700 ${popupImageUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                              data-testid="welcome-popup-image-upload-label"
                            >
                              {popupImageUploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                              {popupImageUploading ? 'Uploading…' : 'Upload Image'}
                              <input
                                type="file"
                                accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                                onChange={(e) => handlePopupImageUpload(e.target.files?.[0])}
                                className="hidden"
                                disabled={popupImageUploading}
                                data-testid="welcome-popup-image-upload-input"
                              />
                            </label>
                            {popupConfig.image_url && (
                              <button
                                type="button"
                                onClick={() => setPopupConfig((p) => ({ ...p, image_url: '' }))}
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-red-600 hover:bg-red-50 font-semibold text-sm"
                                data-testid="welcome-popup-image-remove"
                              >
                                <Trash2 size={14} /> Remove
                              </button>
                            )}
                          </div>
                          <input
                            type="text"
                            value={popupConfig.image_url}
                            onChange={(e) => setPopupConfig((p) => ({ ...p, image_url: e.target.value }))}
                            placeholder="Or paste an image URL"
                            className="w-full mt-2 px-4 py-2.5 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none text-sm"
                            data-testid="welcome-popup-image-url-input"
                          />
                          <p className="text-xs text-gray-400 mt-1">PNG, JPG, GIF or WebP. Max 5MB.</p>
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-800 mb-2">Button Text</label>
                          <input
                            type="text"
                            value={popupConfig.button_text}
                            onChange={(e) => setPopupConfig((p) => ({ ...p, button_text: e.target.value }))}
                            placeholder="Let's Go!"
                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none"
                            data-testid="welcome-popup-button-input"
                            maxLength={40}
                          />
                        </div>

                        <div className="flex items-center gap-3 p-4 rounded-xl bg-gray-50 border border-gray-100">
                          <input
                            type="checkbox"
                            id="popup-logged-in"
                            checked={popupConfig.show_to_logged_in}
                            onChange={(e) => setPopupConfig((p) => ({ ...p, show_to_logged_in: e.target.checked }))}
                            className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                            data-testid="welcome-popup-show-loggedin-toggle"
                          />
                          <label htmlFor="popup-logged-in" className="text-sm text-gray-700 select-none cursor-pointer">
                            Also show to logged-in users (uncheck to show only to guests)
                          </label>
                        </div>

                        <div className="flex items-center gap-3 pt-2 flex-wrap">
                          <button
                            onClick={handleSavePopup}
                            disabled={popupSaving}
                            className="px-6 py-3 bg-black text-white rounded-xl font-bold hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                            data-testid="welcome-popup-save-btn"
                          >
                            {popupSaving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                            {popupSaving ? 'Saving…' : 'Save Changes'}
                          </button>
                          <button
                            onClick={() => setPopupPreview(true)}
                            className="px-5 py-3 bg-emerald-50 text-emerald-700 rounded-xl font-bold hover:bg-emerald-100 flex items-center gap-2"
                            data-testid="welcome-popup-preview-btn"
                          >
                            <Eye size={16} /> Preview
                          </button>
                          {popupSaved && (
                            <span className="text-emerald-600 font-semibold text-sm flex items-center gap-1" data-testid="welcome-popup-saved-indicator">
                              <Check size={16} /> Saved
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Live mini-preview */}
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Live Preview</p>
                        <div className="bg-gray-100 rounded-2xl p-6 border border-gray-200">
                          <div className="bg-white rounded-2xl shadow-lg overflow-hidden max-w-sm mx-auto">
                            {popupConfig.image_url && (
                              <img
                                src={popupConfig.image_url}
                                alt="Preview"
                                className="w-full h-48 object-cover"
                              />
                            )}
                            <div className="p-5 space-y-3">
                              {popupConfig.title && (
                                <h3 className="text-xl font-black text-gray-900 leading-tight">{popupConfig.title}</h3>
                              )}
                              {popupConfig.body && (
                                <div className="text-sm text-gray-700 prose prose-sm max-w-none [&_a]:text-emerald-600 [&_strong]:font-bold [&_p]:my-1.5">
                                  <ReactMarkdown>{popupConfig.body}</ReactMarkdown>
                                </div>
                              )}
                              <button className="w-full py-3 bg-black text-white rounded-xl font-bold text-sm">
                                {popupConfig.button_text || "Let's Go!"}
                              </button>
                            </div>
                          </div>
                          {!popupConfig.enabled && (
                            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 mt-4 text-center">
                              Popup is currently <strong>inactive</strong> — visitors won&apos;t see it.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Full-size preview modal */}
                  {popupPreview && (
                    <div
                      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
                      onClick={() => setPopupPreview(false)}
                      data-testid="welcome-popup-preview-modal"
                    >
                      <div
                        className="bg-white rounded-3xl max-w-lg w-full shadow-2xl overflow-hidden relative"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={() => setPopupPreview(false)}
                          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/90 hover:bg-white text-gray-700 shadow-sm"
                          data-testid="welcome-popup-preview-close"
                        >
                          <X size={18} />
                        </button>
                        {popupConfig.image_url && (
                          <img src={popupConfig.image_url} alt="Preview" className="w-full max-h-72 object-cover" />
                        )}
                        <div className="p-6 sm:p-8 space-y-4">
                          {popupConfig.title && (
                            <h2 className="text-2xl sm:text-3xl font-black text-gray-900 leading-tight">{popupConfig.title}</h2>
                          )}
                          {popupConfig.body && (
                            <div className="text-gray-700 text-base leading-relaxed prose prose-sm max-w-none [&_a]:text-emerald-600 [&_strong]:font-bold [&_p]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5">
                              <ReactMarkdown>{popupConfig.body}</ReactMarkdown>
                            </div>
                          )}
                          <button
                            onClick={() => setPopupPreview(false)}
                            className="w-full py-4 bg-black text-white rounded-xl font-bold text-lg hover:bg-gray-800"
                          >
                            {popupConfig.button_text || "Let's Go!"}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Welcome Back Tab */}
              {activeTab === 'welcome-back' && (
                <div className="bg-white rounded-3xl border border-gray-200 overflow-hidden" data-testid="welcome-back-panel">
                  <div className="p-6 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                        <MapPin size={20} className="text-emerald-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">Welcome Back Greeting</h3>
                        <p className="text-sm text-gray-500">Shown on the mobile home to returning users when their location changes or they&apos;ve been away.</p>
                      </div>
                    </div>
                  </div>

                  {wbLoading ? (
                    <div className="p-10 flex justify-center"><Loader2 size={24} className="animate-spin text-gray-400" /></div>
                  ) : (
                    <div className="p-6 space-y-5 max-w-2xl">
                      {/* Analytics */}
                      <div data-testid="welcome-back-analytics">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Engagement</p>
                        <div className="grid grid-cols-3 gap-3">
                          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 text-center">
                            <p className="text-2xl font-black text-gray-900" data-testid="wb-stat-shown">{wbAnalytics?.totals?.shown ?? 0}</p>
                            <p className="text-xs font-semibold text-gray-500 mt-1">Shown</p>
                          </div>
                          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 text-center">
                            <p className="text-2xl font-black text-emerald-700" data-testid="wb-stat-tapped">{wbAnalytics?.totals?.tapped ?? 0}</p>
                            <p className="text-xs font-semibold text-emerald-600 mt-1">Tapped</p>
                          </div>
                          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 text-center">
                            <p className="text-2xl font-black text-gray-900" data-testid="wb-stat-dismissed">{wbAnalytics?.totals?.dismissed ?? 0}</p>
                            <p className="text-xs font-semibold text-gray-500 mt-1">Dismissed</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 mt-3 text-sm">
                          <span className="text-gray-600">Tap rate: <strong className="text-gray-900">{Math.round((wbAnalytics?.tap_rate ?? 0) * 100)}%</strong></span>
                          <span className="text-gray-600">Dismiss rate: <strong className="text-gray-900">{Math.round((wbAnalytics?.dismiss_rate ?? 0) * 100)}%</strong></span>
                        </div>
                        {wbAnalytics?.daily?.length > 0 && (
                          <div className="mt-4 border border-gray-200 rounded-2xl overflow-hidden">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50 text-gray-500">
                                <tr>
                                  <th className="text-left px-4 py-2 font-semibold">Date</th>
                                  <th className="text-right px-4 py-2 font-semibold">Shown</th>
                                  <th className="text-right px-4 py-2 font-semibold">Tapped</th>
                                  <th className="text-right px-4 py-2 font-semibold">Dismissed</th>
                                </tr>
                              </thead>
                              <tbody>
                                {wbAnalytics.daily.slice().reverse().map((d) => (
                                  <tr key={d.date} className="border-t border-gray-100">
                                    <td className="px-4 py-2 text-gray-700">{d.date}</td>
                                    <td className="px-4 py-2 text-right text-gray-900">{d.shown}</td>
                                    <td className="px-4 py-2 text-right text-emerald-700">{d.tapped}</td>
                                    <td className="px-4 py-2 text-right text-gray-500">{d.dismissed}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>

                      <div className="border-t border-gray-100 pt-5">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Settings</p>
                      </div>

                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={wbConfig.enabled}
                          onChange={(e) => setWbConfig({ ...wbConfig, enabled: e.target.checked })}
                          className="w-5 h-5 rounded"
                          data-testid="welcome-back-enabled-toggle"
                        />
                        <span className="font-semibold text-gray-900">Enabled</span>
                      </label>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Greeting</label>
                        <input
                          type="text"
                          value={wbConfig.greeting}
                          onChange={(e) => setWbConfig({ ...wbConfig, greeting: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-black"
                          data-testid="welcome-back-greeting-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">Use <code className="bg-gray-100 px-1 rounded">{'{country}'}</code> — it&apos;s replaced with the detected country name.</p>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Subtext</label>
                        <input
                          type="text"
                          value={wbConfig.subtext}
                          onChange={(e) => setWbConfig({ ...wbConfig, subtext: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-black"
                          data-testid="welcome-back-subtext-input"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Button text</label>
                        <input
                          type="text"
                          value={wbConfig.button_text}
                          onChange={(e) => setWbConfig({ ...wbConfig, button_text: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-black"
                          data-testid="welcome-back-button-input"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Show if inactive for (days)</label>
                        <input
                          type="number"
                          min={1}
                          value={wbConfig.inactivity_days}
                          onChange={(e) => setWbConfig({ ...wbConfig, inactivity_days: parseInt(e.target.value) || 1 })}
                          className="w-32 px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-black"
                          data-testid="welcome-back-inactivity-input"
                        />
                      </div>

                      <div className="space-y-2 pt-1">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={wbConfig.trigger_on_location_change}
                            onChange={(e) => setWbConfig({ ...wbConfig, trigger_on_location_change: e.target.checked })}
                            className="w-5 h-5 rounded"
                            data-testid="welcome-back-trigger-location"
                          />
                          <span className="text-sm text-gray-700">Trigger when the user&apos;s location changes</span>
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={wbConfig.trigger_on_inactivity}
                            onChange={(e) => setWbConfig({ ...wbConfig, trigger_on_inactivity: e.target.checked })}
                            className="w-5 h-5 rounded"
                            data-testid="welcome-back-trigger-inactivity"
                          />
                          <span className="text-sm text-gray-700">Trigger after the inactivity period above</span>
                        </label>
                      </div>

                      <div className="flex items-center gap-3 pt-2">
                        <button
                          onClick={handleSaveWelcomeBack}
                          disabled={wbSaving}
                          className="px-6 py-3 bg-black text-white rounded-xl font-bold hover:bg-gray-800 disabled:bg-gray-400 flex items-center gap-2"
                          data-testid="welcome-back-save-btn"
                        >
                          {wbSaving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                          {wbSaving ? 'Saving…' : 'Save Changes'}
                        </button>
                        {wbSaved && (
                          <span className="text-emerald-600 font-semibold text-sm flex items-center gap-1" data-testid="welcome-back-saved-indicator">
                            <Check size={16} /> Saved
                          </span>
                        )}
                      </div>

                      <div className="bg-gray-100 rounded-2xl p-5 border border-gray-200 mt-2">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Preview</p>
                        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 max-w-sm">
                          <p className="text-lg font-black text-gray-900">{(wbConfig.greeting || 'Still in {country}?').replace('{country}', 'Japan')}</p>
                          {wbConfig.subtext && <p className="text-sm text-gray-600 mt-1">{wbConfig.subtext}</p>}
                          <span className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-emerald-600 text-white rounded-full text-sm font-bold">{wbConfig.button_text || 'View local plans'} →</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

            </>
          )}
        </div>
      </div>

      {/* Order Details Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white rounded-t-3xl">
              <h2 className="text-xl font-bold text-gray-900">Order Details</h2>
              <div className="flex items-center gap-2">
                {selectedOrder.iccid && (
                  <button
                    onClick={() => setComplimentaryTopupOrder(selectedOrder)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700"
                    data-testid="order-comp-topup-btn"
                    title="Give this eSIM a complimentary top-up"
                  >
                    <Plus size={14} />
                    Complimentary top-up
                  </button>
                )}
                <button
                  onClick={() => { setSelectedOrder(null); setOrderUsage(null); }}
                  className="p-2 hover:bg-gray-100 rounded-xl"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Order Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-sm text-gray-500 mb-1">Customer</p>
                  <p className="font-semibold text-gray-900">{selectedOrder.user_email}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-sm text-gray-500 mb-1">Destination</p>
                  <p className="font-semibold text-gray-900">{selectedOrder.country_name}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-sm text-gray-500 mb-1">Plan</p>
                  <p className="font-semibold text-gray-900">{selectedOrder.plan_name}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-sm text-gray-500 mb-1">Price Paid</p>
                  <p className="font-semibold text-gray-900">${selectedOrder.price?.toFixed(2)}</p>
                </div>
              </div>

              {/* ICCID (always show; supports editing for admin) */}
              <div className="bg-gray-50 rounded-xl p-4" data-testid="iccid-block">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <p className="text-sm text-gray-500">ICCID</p>
                  <div className="flex items-center gap-3">
                    {selectedOrder.iccid && !editingIccid && (
                      <button
                        onClick={() => copyToClipboard(selectedOrder.iccid, 'iccid')}
                        className="text-emerald-600 hover:text-emerald-800 text-sm font-semibold flex items-center gap-1"
                        data-testid="copy-iccid-btn"
                      >
                        {copiedField === 'iccid' ? <CheckCircle size={14} /> : <Copy size={14} />}
                        {copiedField === 'iccid' ? 'Copied!' : 'Copy'}
                      </button>
                    )}
                    {!editingIccid && (
                      <button
                        onClick={handleStartIccidEdit}
                        className="text-blue-600 hover:text-blue-800 text-sm font-semibold flex items-center gap-1"
                        data-testid="edit-iccid-btn"
                      >
                        <Wrench size={14} /> {selectedOrder.iccid ? 'Edit' : 'Add ICCID'}
                      </button>
                    )}
                  </div>
                </div>

                {editingIccid ? (
                  <div className="space-y-3 mt-2">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">ICCID *</label>
                      <input
                        type="text"
                        value={iccidForm.iccid}
                        onChange={(e) => { setIccidForm((f) => ({ ...f, iccid: e.target.value })); setIccidError(''); }}
                        placeholder="e.g. 89481234567890123456"
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none font-mono text-sm"
                        data-testid="iccid-input"
                        disabled={iccidSaving}
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">SM-DP+ Address</label>
                        <input
                          type="text"
                          value={iccidForm.smdp_address}
                          onChange={(e) => setIccidForm((f) => ({ ...f, smdp_address: e.target.value }))}
                          placeholder="rsp.example.com"
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none font-mono text-sm"
                          data-testid="smdp-input"
                          disabled={iccidSaving}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Matching ID</label>
                        <input
                          type="text"
                          value={iccidForm.matching_id}
                          onChange={(e) => setIccidForm((f) => ({ ...f, matching_id: e.target.value }))}
                          placeholder="ABCD-1234-EFGH"
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none font-mono text-sm"
                          data-testid="matching-id-input"
                          disabled={iccidSaving}
                        />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">
                      Providing both SM-DP+ and Matching ID will regenerate the QR code and iOS/Android one-tap install URLs.
                    </p>
                    {iccidError && (
                      <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-2" data-testid="iccid-error">
                        {iccidError}
                      </p>
                    )}
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        onClick={handleSaveIccid}
                        disabled={iccidSaving}
                        className="px-4 py-2 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 disabled:bg-gray-400 flex items-center gap-2 text-sm"
                        data-testid="save-iccid-btn"
                      >
                        {iccidSaving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                        {iccidSaving ? 'Validating & Saving…' : 'Save'}
                      </button>
                      <button
                        onClick={handleCancelIccidEdit}
                        disabled={iccidSaving}
                        className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 text-sm"
                        data-testid="cancel-iccid-btn"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="font-mono text-gray-900 break-all" data-testid="iccid-display">
                    {selectedOrder.iccid || <span className="text-gray-400 italic">Not assigned</span>}
                  </p>
                )}
              </div>

              {/* Usage Data */}
              {loadingUsage ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
                </div>
              ) : orderUsage ? (
                <div className="bg-emerald-50 rounded-xl p-6">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Wifi className="text-emerald-600" /> Usage Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Status</p>
                      <span className={`inline-block mt-1 px-3 py-1 rounded-full text-xs font-bold ${
                        orderUsage.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                        orderUsage.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {orderUsage.status}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Data Remaining</p>
                      <p className="text-2xl font-bold text-emerald-600 flex items-center gap-1">
                        {(orderUsage.allowed_data_gb > 100000 || orderUsage.remaining_data_gb > 100000) ? (
                          <span className="flex items-center gap-1">∞ <span className="text-sm font-normal text-gray-500">Unlimited</span></span>
                        ) : (
                          <>{orderUsage.remaining_data_gb?.toFixed(2) || '0'} GB</>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Total Data</p>
                      <p className="font-semibold text-gray-900">
                        {orderUsage.allowed_data_gb > 100000 ? (
                          <span className="flex items-center gap-1">∞ <span className="text-sm font-normal text-gray-500">Unlimited</span></span>
                        ) : (
                          <>{orderUsage.allowed_data_gb?.toFixed(2) || '0'} GB</>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Expires</p>
                      <p className="font-semibold text-gray-900">
                        {orderUsage.expired_at 
                          ? new Date(orderUsage.expired_at * 1000).toLocaleDateString('en-US', { 
                              year: 'numeric', month: 'short', day: 'numeric' 
                            })
                          : 'Not activated'}
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}

              {/* Install Links */}
              {selectedOrder.lpa_code && (
                <div>
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Smartphone className="text-gray-600" /> Installation Links
                  </h3>
                  <div className="space-y-3">
                    {/* iOS Link */}
                    <div className="bg-gray-900 text-white rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold">🍎 iOS One-Tap Install</span>
                        <button
                          onClick={() => copyToClipboard(`https://esimsetup.apple.com/esim_qrcode_provisioning?carddata=${selectedOrder.lpa_code}`, 'ios')}
                          className="text-emerald-400 hover:text-emerald-300 text-sm font-semibold flex items-center gap-1"
                        >
                          {copiedField === 'ios' ? <CheckCircle size={14} /> : <Copy size={14} />}
                          {copiedField === 'ios' ? 'Copied!' : 'Copy Link'}
                        </button>
                      </div>
                      <a 
                        href={`https://esimsetup.apple.com/esim_qrcode_provisioning?carddata=${selectedOrder.lpa_code}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-gray-400 hover:text-white break-all flex items-center gap-1"
                      >
                        <ExternalLink size={12} /> Open Link
                      </a>
                    </div>

                    {/* Android Link */}
                    <div className="bg-green-600 text-white rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold">🤖 Android Install</span>
                        <button
                          onClick={() => copyToClipboard(selectedOrder.lpa_code, 'android')}
                          className="text-green-200 hover:text-white text-sm font-semibold flex items-center gap-1"
                        >
                          {copiedField === 'android' ? <CheckCircle size={14} /> : <Copy size={14} />}
                          {copiedField === 'android' ? 'Copied!' : 'Copy LPA'}
                        </button>
                      </div>
                      <p className="text-xs text-green-200 break-all font-mono">{selectedOrder.lpa_code}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* QR Code */}
              {selectedOrder.qr_code_url && (
                <div>
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <QrCode className="text-gray-600" /> QR Code Install
                  </h3>
                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6 text-center">
                    <img 
                      src={selectedOrder.qr_code_url} 
                      alt="eSIM QR Code" 
                      className="w-48 h-48 mx-auto rounded-lg"
                    />
                    <p className="text-sm text-gray-500 mt-4">
                      Customer can scan this QR code with their phone camera to install
                    </p>
                  </div>
                </div>
              )}

              {/* LPA Code (fallback if no QR) */}
              {selectedOrder.lpa_code && !selectedOrder.qr_code_url && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-gray-500">LPA Code (Manual Entry)</p>
                    <button
                      onClick={() => copyToClipboard(selectedOrder.lpa_code, 'lpa')}
                      className="text-emerald-600 hover:text-emerald-800 text-sm font-semibold flex items-center gap-1"
                    >
                      {copiedField === 'lpa' ? <CheckCircle size={14} /> : <Copy size={14} />}
                      {copiedField === 'lpa' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <p className="font-mono text-xs text-gray-900 break-all">{selectedOrder.lpa_code}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Credits Modal */}
      {showAddCreditsModal && selectedUserForCredits && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Add Credits</h2>
              <button
                onClick={() => {
                  setShowAddCreditsModal(false);
                  setSelectedUserForCredits(null);
                  setCreditsToAdd('');
                  setCreditsReason('');
                }}
                className="p-2 hover:bg-gray-100 rounded-xl"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm text-gray-500 mb-1">User</p>
                <p className="font-semibold text-gray-900">{selectedUserForCredits.email}</p>
                <p className="text-sm text-gray-500 mt-1">
                  Current Balance: A${(selectedUserForCredits.credit_balance_aud || 0).toFixed(2)}
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Amount to Add (AUD)
                </label>
                <input
                  type="number"
                  value={creditsToAdd}
                  onChange={(e) => setCreditsToAdd(e.target.value)}
                  min="0"
                  step="0.5"
                  placeholder="5.00"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-black"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Reason (optional)
                </label>
                <input
                  type="text"
                  value={creditsReason}
                  onChange={(e) => setCreditsReason(e.target.value)}
                  placeholder="e.g., Customer support credit"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-black"
                />
              </div>

              <button
                onClick={handleAddCredits}
                disabled={!creditsToAdd || parseFloat(creditsToAdd) <= 0}
                className="w-full px-6 py-3 bg-black text-white rounded-xl font-bold hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add A${creditsToAdd || '0.00'} Credits
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Confirmation Modal */}
      {showDeleteConfirm && userToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-red-600">Delete User</h2>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setUserToDelete(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-xl"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-red-800">Warning: This action cannot be undone!</p>
                    <p className="text-sm text-red-600 mt-1">
                      All data associated with this user will be permanently deleted.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm text-gray-500 mb-1">User to delete</p>
                <p className="font-semibold text-gray-900">{userToDelete.name}</p>
                <p className="text-sm text-gray-600">{userToDelete.email}</p>
              </div>

              <div className="text-sm text-gray-600">
                <p className="font-medium mb-2">This will delete:</p>
                <ul className="list-disc list-inside space-y-1 text-gray-500">
                  <li>User account</li>
                  <li>All orders and eSIMs</li>
                  <li>Payment history</li>
                  <li>Referral records</li>
                  <li>Credit balance</li>
                  <li>Chat history</li>
                </ul>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setUserToDelete(null);
                  }}
                  className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteUser}
                  disabled={deletingUser}
                  className="flex-1 px-6 py-3 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="confirm-delete-user"
                >
                  {deletingUser ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Delete User
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Order Confirmation Modal */}
      {showDeleteOrderConfirm && orderToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-red-600">Delete Order</h2>
              <button
                onClick={() => {
                  setShowDeleteOrderConfirm(false);
                  setOrderToDelete(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-xl"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-red-800">Warning: This action cannot be undone!</p>
                    <p className="text-sm text-red-600 mt-1">
                      This order will be permanently deleted from the system.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                <div>
                  <p className="text-sm text-gray-500">Order ID</p>
                  <p className="font-mono text-sm text-gray-900">{orderToDelete.id}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Customer</p>
                  <p className="font-semibold text-gray-900">{orderToDelete.user_email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Destination</p>
                  <p className="font-semibold text-gray-900">{orderToDelete.country_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Plan</p>
                  <p className="text-gray-700">{orderToDelete.plan_name}</p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteOrderConfirm(false);
                    setOrderToDelete(null);
                  }}
                  className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteOrder}
                  disabled={deletingOrder}
                  className="flex-1 px-6 py-3 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="confirm-delete-order"
                >
                  {deletingOrder ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Delete Order
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {complimentaryEsimUser && (
        <AddComplimentaryEsimModal
          customer={complimentaryEsimUser}
          onClose={() => setComplimentaryEsimUser(null)}
          onCreated={async () => {
            try {
              const [u, o] = await Promise.all([getAllUsers(), getAllOrders()]);
              setCustomers(u);
              setOrders(o);
            } catch (_) { /* silent */ }
          }}
        />
      )}

      {complimentaryTopupOrder && (
        <ComplimentaryTopupModal
          order={complimentaryTopupOrder}
          onClose={() => setComplimentaryTopupOrder(null)}
          onCompleted={async () => {
            try {
              const o = await getAllOrders();
              setOrders(o);
            } catch (_) { /* silent */ }
          }}
        />
      )}

      <Footer />
    </div>
  );
};

export default AdminDashboard;
